import { normalizeActiveProgram, activeProgramFromWire } from './utils/programEnrollment';
import React, { useState, useEffect, useRef, useCallback, useReducer, useMemo } from 'react';
import { V, Haptic, onThemeChange } from './utils/theme';
import { applyBrand, appName } from './utils/brand';
import { OrgContext, readOrgCache, writeOrgCache, clearOrgCache, orgApi, orgErr, errorMessage, flagsFrom, isActiveOrg, isSuspendedOrg, memberBrand,
  setFlagsSnapshot, emitOrgPing, applyManifest, PENDING_JOIN_KEY, OPEN_INBOX_KEY, TAB_FLAGS } from './utils/org';
import { JoinLanding, JoinRedeem, SuspendedSheet } from './components/JoinFlow';
import { OrgInbox } from './tabs/OrgInbox';
import { LS, Cookie } from './utils/storage';
import { AuthToken, SessionManager } from './utils/auth';
import { uid, today, ago, fmtShort, isIOS, isStandalone } from './utils/helpers';
import { SYNC_URL, APP_VERSION, CloudSync, SocialAPI, MsgBannerCtrl_ref, setMsgBannerCtrlRef, setFeatureDisabledHandler } from './utils/sync';
import { subscribeDMs, subscribeOrg } from './utils/realtime';
import { applyMessagePoll, unreadMessageCount, ensureMessageAccount } from './utils/messagePolling';
import { Icons } from './components/Icons';
import { Btn, UndoToast, GlobalConfirm, SuccessToast, SuccessToastCtrl, MessageBanner, MsgBannerCtrl, ReauthModal, ReauthCtrl } from './components/ui';
import { CheckinModal } from './components/dialogs';
import { defaultExercises } from './data/exercises';
import { reducer, init, defaultSchedule } from './state/reducer';
import {
  hydrateLocalHealth, phaseIsActive, phaseTypeId,
} from './utils/healthWire';
import { HomeTab } from './tabs/HomeTab';
import { WorkoutTab } from './tabs/WorkoutTab';
import { NutritionTab } from './tabs/NutritionTab';
import { BodyTab } from './tabs/BodyTab';
import { CalendarTab } from './tabs/CalendarTab';
import { AnalyticsTab, ExerciseChart, DurationTrends, VolumeTracker, MuscleHeatMap, StrengthScoreCard } from './tabs/AnalyticsTab';
import { SettingsTab } from './tabs/SettingsTab';
import { Onboarding } from './tabs/Onboarding';
import { LogHub, TrackHub, PlanHub } from './tabs/hubs';
import {
  OneRMCalc, ProgressPhotos, AdminPanel, GoalEngine, AdaptiveCoach,
  FormCheckTab, DataGuardTab, PhaseTracker, SubstitutionFinder, InjuryManager,
  WeeklySummary, FastFoodHacks, MealPlanGenerator, ProgramMarketplace,
  SupplementTracker, PersonalRecords, PhotoCompare, AICoachChat,
} from './tabs/features';
import { AdminPush, AdminUserAudit, AdminXPManager, AdminBusinessValue, AdminHub, AdminOrgs, OrgAnalytics } from './tabs/admin';
import {
  SocialTab, SocialFeed, SocialFriends, MessagesTab, SocialGroups,
  SocialProfile, SocialBadges, SocialNotifications, SocialCompare,
  SocialLeaderboard, SocialChallenges, calcIronScore, SocialLeague } from './tabs/social';
import { GamificationOverlay, SocialIronScore, SocialDuels, SocialWeeklyWar, SocialRivals } from './tabs/gamification';
import { ReadinessTrend } from './components/dialogs';
import { useLayout } from './utils/responsive';
import { IconTile, ProgressSteps, LinkBtn, Callout, NumberedStep, LogoMark, onAccent } from './components/kit';
import { InstallGuide } from './tabs/onboarding/ui';
import { EmailVerifyPrompt } from './components/EmailVerify';

// Header wordmark: "IRON"+gradient "LOG" for solo users, the org's app name (plain) when branded
const BrandTitle=({size})=>V.brand?.app_name
  ?<span style={{fontSize:size,fontWeight:800,letterSpacing:"-0.03em",color:V.text,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis",maxWidth:180}}>{V.brand.app_name}</span>
  :<span style={{fontSize:size,fontWeight:800,letterSpacing:"-0.03em",color:V.text}}>
      IRON<span style={{background:`linear-gradient(90deg,${V.accent},${V.accent2})`,
        WebkitBackgroundClip:"text",backgroundClip:"text",WebkitTextFillColor:"transparent"}}>LOG</span>
    </span>;

// "Powered by IronLog" line under the bottom nav — only when the org's plan requires the badge
const PoweredBy=({inSidebar})=>(
  <a href="https://ironlog.space" target="_blank" rel="noopener noreferrer" aria-label="Powered by IronLog" data-testid="powered-by"
    style={{display:"flex",alignItems:"center",justifyContent:"center",gap:5,height:20,flexShrink:0,
      background:inSidebar?"transparent":V.navBg,paddingBottom:inSidebar?"max(8px, env(safe-area-inset-bottom, 0px))":"env(safe-area-inset-bottom, 0px)",
      boxSizing:"content-box",textDecoration:"none",fontSize:10,fontWeight:600,color:V.text3,letterSpacing:".02em",
      position:"relative",zIndex:50,marginTop:inSidebar?"auto":0,WebkitTapHighlightColor:"transparent"}}>
    <span>Powered by</span>
    <span style={{fontWeight:800,color:V.text2}}>IRON<span style={{color:V.accent}}>LOG</span></span>
  </a>
);

const Footer=()=>(
  <div style={{textAlign:"center",padding:"22px 0 8px",borderTop:`1px solid ${V.divider}`,marginTop:18}}>
    <div style={{display:"inline-flex",alignItems:"center",gap:6,marginBottom:6}}>
      <LogoMark size={18} radius={6}/>
      <span style={{fontSize:11,fontWeight:800,letterSpacing:".02em",color:V.text2}}>{appName()}</span>
    </div>
    <div style={{fontSize:11,fontWeight:600,color:V.text3}}>Made by <span style={{color:V.text2,fontWeight:700}}>Wiseforge</span></div>
    <div style={{display:"flex",justifyContent:"center",gap:12,marginTop:4}}>
      <a href="https://wiseforge.dev" target="_blank" rel="noopener noreferrer" style={{fontSize:10,color:V.accent,textDecoration:"none",fontWeight:600}}>Wiseforge.dev</a>
      <span style={{fontSize:10,color:V.text3}}>·</span>
      <a href="https://ironlog.space" target="_blank" rel="noopener noreferrer" style={{fontSize:10,color:V.accent,textDecoration:"none",fontWeight:600}}>Ironlog.space</a>
    </div>
    <div style={{fontSize:9,color:V.text3,marginTop:4,opacity:0.6}}>v{APP_VERSION} · For updates visit ironlog.space</div>
  </div>
);

function App(){
  const [s,d]=useReducer(reducer,init);
  const contentRef=useRef(null);
  const { isDesktop, isTablet, isMobile, showSidebar, sidebarWidth, contentMaxWidth } = useLayout();

  // ─── Swipe Gestures — navigate between main tabs ───
  // "admin" intentionally excluded: it only renders for admins, so including it let a
  // non-admin swipe past Settings into a blank tab. Admins still reach it via the nav item.
  const mainTabs=["home","log","track","plan","social","settings"];
  const swipeRef=useRef({startX:0,startY:0,startTime:0});
  const handleTouchStart=useCallback((e)=>{
    // Don't hijack horizontal gestures that begin inside a horizontally scrollable strip,
    // a chart, or a form control — those own their own horizontal movement.
    let skip=false;
    try{
      const t=e.target;
      if(t.closest&&t.closest("input,textarea,select,.recharts-wrapper,[data-no-swipe]"))skip=true;
      let el=t;
      while(!skip&&el&&el!==e.currentTarget){
        if(el.scrollWidth>el.clientWidth+1){
          const ov=getComputedStyle(el).overflowX;
          if(ov==="auto"||ov==="scroll"){skip=true;break;}
        }
        el=el.parentElement;
      }
    }catch{}
    swipeRef.current={startX:e.touches[0].clientX,startY:e.touches[0].clientY,startTime:Date.now(),skip};
  },[]);
  const handleTouchEnd=useCallback((e)=>{
    const {startX,startY,startTime,skip}=swipeRef.current;
    if(skip)return;
    const dx=e.changedTouches[0].clientX-startX;
    const dy=e.changedTouches[0].clientY-startY;
    const dt=Date.now()-startTime;
    // Only trigger on fast horizontal swipes (not scrolling)
    if(Math.abs(dx)>80&&Math.abs(dy)<80&&dt<400){
      // Find current parent tab
      const activeParent2=mainTabs.find(t=>s.tab===t||s.tab.startsWith(t+"_"))||"home";
      const idx=mainTabs.indexOf(activeParent2);
      if(dx<-80&&idx<mainTabs.length-1){Haptic.light();d({type:"TAB",tab:mainTabs[idx+1]});}
      else if(dx>80&&idx>0){Haptic.light();d({type:"TAB",tab:mainTabs[idx-1]});}
    }
  },[s.tab]);
  const [installPrompt,setInstallPrompt]=useState(null);
  const [showInstallBanner,setShowInstallBanner]=useState(false);
  const [showUpdateBanner,setShowUpdateBanner]=useState(false);
  const [isOnline,setIsOnline]=useState(navigator.onLine);
  const [pushHealth,setPushHealth]=useState(null);
  const [showInstallGuide,setShowInstallGuide]=useState(false);
  const [,bumpBanner]=useReducer(x=>x+1,0); // re-render after localStorage-only dismissals

  // #1: Online/offline detection
  useEffect(()=>{
    const on=()=>setIsOnline(true);
    const off=()=>setIsOnline(false);
    window.addEventListener("online",on);
    window.addEventListener("offline",off);
    return()=>{window.removeEventListener("online",on);window.removeEventListener("offline",off);};
  },[]);

  // #2: Register periodic sync + #3: Push health — single listener with cleanup
  useEffect(()=>{
    if(!("serviceWorker" in navigator)||!s.onboarded)return;
    const handler=(e)=>{
      if(e.data?.type==="BACKGROUND_SYNC"&&s.profile?.email){
        const doSync=window.__ironlog_sync;if(doSync)doSync();
      }
      if(e.data?.type==="PUSH_HEALTH")setPushHealth(e.data);
      if(e.data?.type==="PUSH_RESUBSCRIBED")setPushHealth({active:true,endpoint:e.data.subscription?.endpoint});
      if(e.data?.type==="PUSH_SUBSCRIPTION_LOST"){
        // Push subscription expired and couldn't auto-renew — show actionable toast
        MsgBannerCtrl.push({name:"Notifications",text:"Push subscription lost. Re-enable in Settings → Notifications.",type:"system"});
        setPushHealth({active:false,endpoint:null});
      }
      if(e.data?.type==="OPEN_DM"&&e.data.from){
        d({type:"TAB",tab:"social_messages"});
        // Store the sender so MessagesTab can auto-open their chat
        LS.set("ft-open-dm-from",e.data.from);
      }
      if(e.data?.type==="OPEN_FEED")d({type:"TAB",tab:"social_feed"});
      // B9: gym push tapped while the app is open → inbox (the flag-gating effect sends it home if the org is gone)
      if(e.data?.type==="OPEN_ORG_INBOX")d({type:"TAB",tab:"org_inbox"});
      if(e.data?.type==="REACT_EVENT"&&e.data.eventId)quickReactFromPush(e.data.eventId);
    };
    navigator.serviceWorker.addEventListener("message",handler);
    navigator.serviceWorker.ready.then(reg=>{
      if("periodicSync" in reg)reg.periodicSync.register("ironlog-periodic-sync",{minInterval:4*60*60*1000}).catch(()=>{});
      if(navigator.serviceWorker.controller)navigator.serviceWorker.controller.postMessage({type:"CHECK_PUSH_HEALTH"});
    }).catch(()=>{});
    return()=>navigator.serviceWorker.removeEventListener("message",handler);
  },[s.onboarded,s.profile?.email]);
  const [socialNotifCount,setSocialNotifCount]=useState(0);
  const [unreadMsgCount,setUnreadMsgCount]=useState(0);
  // tabRef: captures current tab without making poll interval depend on s.tab
  const tabRef=useRef(s.tab);
  useEffect(()=>{tabRef.current=s.tab;},[s.tab]);

  // One-tap "React" from a PR / streak push notification. Fires a 🔥 on the friend's
  // event and confirms with a toast; the feed tab shows the result.
  const quickReactFromPush=async(eventId)=>{
    const em=s.profile?.email||LS.get("ft-session-email");
    if(!em)return;
    const r=await SocialAPI.react(em,eventId,"fire");
    if(r){LS.set("ft-reacted-today",today());SuccessToastCtrl.show("🔥 Reaction sent");}
    else SuccessToastCtrl.error("Could not react — open the feed to try again");
    d({type:"TAB",tab:"social_feed"});
  };

  // On launch from push notification: read ?open-dm= / ?react-event= / ?open-feed= URL params
  useEffect(()=>{
    const params=new URLSearchParams(window.location.search);
    const reactEvent=params.get("react-event");
    if(reactEvent||params.get("open-feed")){
      window.history.replaceState({},"",window.location.pathname+(window.location.hash||""));
      if(reactEvent)setTimeout(()=>quickReactFromPush(reactEvent),800);else d({type:"TAB",tab:"social_feed"});
    }
    // White-label: ?join=CODE (invite) and ?open-org-inbox=1 (email/push deep link). Distinct from
    // ?code= / ?add-friend= (friend codes) so the two flows never collide.
    const joinCode=params.get("join");
    if(joinCode){
      const code=String(joinCode).toUpperCase().replace(/[^A-Z0-9]/g,"").slice(0,16);
      window.history.replaceState({},"",window.location.pathname+(window.location.hash||""));
      if(code){LS.set(PENDING_JOIN_KEY,code);setPendingJoin(code);setJoinStage("landing");}
    }
    if(params.get("open-org-inbox")){
      window.history.replaceState({},"",window.location.pathname+(window.location.hash||""));
      LS.set(OPEN_INBOX_KEY,true);
    }
    const openDm=params.get("open-dm");
    if(openDm){
      LS.set("ft-open-dm-from",openDm);
      // Clean the URL without a reload
      const clean=window.location.pathname+(window.location.hash||"");
      window.history.replaceState({},"",clean);
      d({type:"TAB",tab:"social_messages"});
    }
  },[]);

  // Deep-link: ?add-friend=IRON-XXXX — works from QR code scans and shared links
  // Step 1: [] effect — capture the code, clean URL immediately (before INIT fires)
  useEffect(()=>{
    const params=new URLSearchParams(window.location.search);
    const addCode=params.get("add-friend")||params.get("code");
    if(!addCode)return;
    // Clean URL so refreshing doesn't re-trigger
    window.history.replaceState({},"",window.location.pathname+(window.location.hash||""));
    LS.set("ft-pending-add",addCode.toUpperCase());
  },[]);
  // Step 2: [s.onboarded] effect — once INIT fires and user is confirmed logged in, navigate
  // B1 fix: s.onboarded is always false in [] effect (INIT hasn't dispatched yet)
  useEffect(()=>{
    if(!s.onboarded)return;
    if(LS.get("ft-pending-add")){
      d({type:"TAB",tab:"social_friends"});
    }
  },[s.onboarded]);

  // Layer 2: Message polling (every 15s when visible)
  useEffect(()=>{
    if(!s.onboarded||!s.profile?.email)return;
    const email=s.profile.email;
    ensureMessageAccount(LS,Object.keys(localStorage),email);
    let pollTimer=null;
    let cancelled=false;
    let polling=false;
    const poll=async()=>{
      if(cancelled||polling||document.visibilityState!=="visible")return;
      polling=true;
      const pollStarted=new Date().toISOString();
      setUnreadMsgCount(unreadMessageCount(LS,Object.keys(localStorage)));
      // Keep the last successful cursor across offline periods; reject invalid or
      // future clocks and use a short lookback when no usable cursor exists.
      const rawPoll=LS.get("ft-last-poll");
      const fiveMinAgo=new Date(Date.now()-300000).toISOString();
      const rawTime=Date.parse(rawPoll);
      const since=Number.isFinite(rawTime)&&rawTime<Date.parse(pollStarted)?new Date(rawTime).toISOString():fiveMinAgo;
      let result;
      try { result=await SocialAPI.checkMessages(email,since); }
      catch { return; }
      finally { polling=false; }
      if(cancelled||!result)return;
      // Advance only through the start of this request: messages arriving while
      // it was in flight must still be fetched by the next poll.
      LS.set("ft-last-poll",pollStarted);
      const fresh=applyMessagePoll(LS,result);
      fresh.forEach(message=>{
        const viewingSocialChat=message.type==="dm"
          ? ["social_friends","social_messages"].includes(tabRef.current)
          : tabRef.current==="social_groups";
        if(!viewingSocialChat)MsgBannerCtrl.push(message);
      });
      setUnreadMsgCount(unreadMessageCount(LS,Object.keys(localStorage)));
      // Also refresh server notification count
      SocialAPI.getNotifications(email).then(r=>{if(!cancelled&&r)setSocialNotifCount(r.unread||0);});
      // ── Gamification live-refresh: admin XP/rank changes propagate within 15s ──
      (async()=>{
        try{
          const localBonus=LS.get("ft-xp-bonus")||{total:0,log:[]};
          const token=LS.get("ft-session-token");
          if(!token)return;
          const r=await fetch(`${SYNC_URL}/api/sync/gamification`,{
            method:"POST",
            headers:{"Content-Type":"application/json","X-Session-Token":token},
            body:JSON.stringify({client_total:localBonus.total||0}),
          });
          if(!r.ok)return;
          const gj=await r.json();
          if(cancelled||!gj.updated)return;
          // Server has a different xp_bonus — apply it
          const prev=localBonus.total||0;
          LS.set("ft-xp-bonus",gj.xp_bonus);
          if(gj.last_known_level!=null)LS.set("ft-last-known-level",gj.last_known_level);
          // Toast the user so they know XP changed
          const diff=(gj.xp_bonus?.total||0)-prev;
          if(diff!==0){
            const msg=diff>0?`⚡ +${diff.toLocaleString()} XP added to your account`:`⚡ XP updated by admin`;
            SuccessToastCtrl.show(msg);
          }
          // Force lightweight re-render so IronScore / rank badge update immediately
          d({type:"XP_REFRESH"});
        }catch(e){}
      })();
    };
    // Initial poll after 3s
    const initTimer=setTimeout(poll,3000);
    // Visibility-aware interval: 15s when visible, 60s when backgrounded
    const getInterval=()=>document.visibilityState==="visible"?15000:60000;
    pollTimer=setInterval(poll,getInterval());
    // Realtime: a DM ping from the API triggers an immediate poll so the badge and banner
    // update in ~1s instead of at the next 15s tick.
    const unsubscribeRT=subscribeDMs(email,()=>{LS.set("ft-last-poll",new Date(Date.now()-60000).toISOString());poll();});
    // On visibility change: re-poll immediately if returning to foreground, restart interval at correct rate
    const visHandler=()=>{
      clearInterval(pollTimer);
      if(document.visibilityState==="visible")poll();
      pollTimer=setInterval(poll,getInterval());
    };
    document.addEventListener("visibilitychange",visHandler);
    return()=>{cancelled=true;clearTimeout(initTimer);clearInterval(pollTimer);document.removeEventListener("visibilitychange",visHandler);unsubscribeRT();};
  },[s.onboarded,s.profile?.email]);  // s.tab removed — read via tabRef to avoid restarting interval on every nav
  const [showUpdateGuide,setShowUpdateGuide]=useState(false);
  const [updateVersion,setUpdateVersion]=useState(null);
  const [updateNotes,setUpdateNotes]=useState("");

  // Check for app updates on load
  useEffect(()=>{
    const avail=LS.get("ft-update-available");
    if(avail&&avail!==APP_VERSION){
      setUpdateVersion(avail);
      setUpdateNotes(LS.get("ft-update-notes")||"");
      // Don't show immediately if dismissed recently
      const dismissed=LS.get("ft-update-dismissed");
      if(dismissed!==avail)setShowUpdateBanner(true);
    }
  },[]);

  // Scroll to top when tab changes
  useEffect(()=>{
    if(contentRef.current)contentRef.current.scrollTop=0;
  },[s.tab]);

  // Expose the real height of the scroll region as --content-h so full-height screens
  // (chat, AI coach) can size themselves exactly. 100vh ignores the header, bottom nav and
  // iOS safe areas, which pushed chat composers below the fold on iPhone.
  useEffect(()=>{
    const el=contentRef.current;
    if(!el||typeof ResizeObserver==="undefined")return;
    const update=()=>{
      const pb=parseFloat(getComputedStyle(el).paddingBottom)||0;
      el.style.setProperty("--content-h",Math.max(0,el.clientHeight-pb)+"px");
    };
    update();
    const ro=new ResizeObserver(update);
    ro.observe(el);
    return()=>ro.disconnect();
  },[]);

  // #10 Track visits on app launch (separate from install prompt)
  useEffect(()=>{
    const v=parseInt(LS.get("ft-visits")||"0")+1;
    LS.set("ft-visits",v);
    LS.set("ft-platform",isIOS()?"ios":/Android/.test(navigator.userAgent)?"android":"desktop");
  },[]);

  // Fetch social notification count
  useEffect(()=>{
    if(s.onboarded&&s.profile?.email){
      SocialAPI.getNotifications(s.profile.email).then(r=>{if(r)setSocialNotifCount(r.unread||0);});
    }
  },[s.onboarded,s.profile?.email]);

  // Clear badge when visiting notifications
  useEffect(()=>{if(s.tab==="social_notifications")setSocialNotifCount(0);},[s.tab]);

  // Admin check (silent, no UI trace if not admin)
  // checkAdmin() resolves to the whole `check` payload: { isAdmin, isSuperAdmin,
  // adminEmailConfigured }. isSuperAdmin gates the tenant screens (Organizations,
  // Org Analytics) — the API answers `super_admin_required` for those actions, so
  // the hub disables the tiles instead of walking an ordinary admin into a 403.
  const [isAdmin,setIsAdmin]=useState(LS.get("ft-is-admin")===true);
  // true = confirmed super-admin · false = confirmed not · null = not answered yet
  // (first load, or an API build whose `check` predates the field). Only an
  // explicit false gates a tile: an unknown must not lock the super-admin out.
  const [isSuperAdmin,setIsSuperAdmin]=useState(()=>{const v=LS.get("ft-is-super-admin");return v===true?true:v===false?false:null;});
  const [adminEmailConfigured,setAdminEmailConfigured]=useState(true);
  useEffect(()=>{
    if(s.onboarded&&s.profile?.email){
      SessionManager.checkAdmin().then(a=>{
        setIsAdmin(!!a.isAdmin);
        setIsSuperAdmin(a.isSuperAdmin??null);
        setAdminEmailConfigured(a.adminEmailConfigured!==false);
      });
    }
  },[s.onboarded,s.profile?.email]);
  // Re-verify admin on every navigation to an admin tab so flipping the LS flag
  // in DevTools doesn't persist beyond one server round-trip. Server is the only
  // authority — this is just defense-in-depth on the client.
  useEffect(()=>{
    if(typeof s.tab==="string" && s.tab.startsWith("admin")){
      SessionManager.checkAdmin().then(a=>{
        if(!a.isAdmin) setIsAdmin(false);
        if(a.isSuperAdmin!==true) setIsSuperAdmin(a.isSuperAdmin??null); // downgrade only
        if(a.isAdmin) setAdminEmailConfigured(a.adminEmailConfigured!==false);
      });
    }
  },[s.tab]);

  // ─── White-label tenant (org) ───
  // Re-render the tree when applyBrand()/setTheme() mutate V
  const [,bumpTheme]=useReducer(x=>x+1,0);
  useEffect(()=>onThemeChange(()=>bumpTheme()),[]);
  // Hydrate from the cache before `me` resolves so a branded app never flashes IronLog green;
  // the brand is applied synchronously here (idempotent) rather than in an effect.
  const [orgMe,setOrgMe]=useState(()=>{
    const c=readOrgCache();
    const cb=memberBrand(c);
    if(cb&&!LS.get(PENDING_JOIN_KEY)){try{applyBrand(cb);}catch(e){}}
    return c;
  });
  const [orgResolved,setOrgResolved]=useState(false);
  const [orgUnread,setOrgUnread]=useState(()=>readOrgCache()?.unread||{messages:0,announcements:0});
  const [pendingJoin,setPendingJoin]=useState(()=>LS.get(PENDING_JOIN_KEY)||null);
  const [joinStage,setJoinStage]=useState(()=>LS.get(PENDING_JOIN_KEY)?"landing":null); // landing → proceed → redeem
  const [joinSignIn,setJoinSignIn]=useState(false);
  // Suspension sheet is acknowledged once per org (LS "ft-org-suspend-ack-<org_id>"), not once per cold start.
  const suspendAckKey=orgMe?.org?.id?"ft-org-suspend-ack-"+orgMe.org.id:null;
  const [suspendedAck,setSuspendedAck]=useState(()=>!!(suspendAckKey&&LS.get(suspendAckKey)));
  // Kill-switch (503 org_features_disabled) notice: null = none, "dismissed" = hidden until `me` succeeds again
  const [orgNotice,setOrgNotice]=useState(null);
  const refreshOrg=useCallback(async()=>{
    if(!SessionManager.hasSession()){setOrgResolved(true);return null;}
    try{
      const me=await orgApi("me");
      setOrgNotice(null);
      if(!me||!me.org){clearOrgCache();setOrgMe(null);return null;}
      const rec=writeOrgCache(me);
      setOrgMe(rec);setOrgUnread(me.unread||{messages:0,announcements:0});
      return rec;
    }catch(e){
      const er=orgErr(e);
      if(er.status===423){ // keep the org, mark it suspended
        setOrgMe(m=>{if(!m)return m;const n={...m,org:{...m.org,status:"suspended",suspended_reason:er.reason||m.org.suspended_reason}};LS.set("ft-org",n);return n;});
      }else if(er.status===503&&er.code==="org_features_disabled"){ // kill switch: keep the cached org/brand, tell the member
        setOrgMe(m=>m?{...m,stale:true}:m);setOrgNotice(n=>n==="dismissed"?n:er);
      }else if(er.status===401||er.status===403){/* session problem — ReauthModal handles it; keep the cache */}
      else setOrgMe(m=>m?{...m,stale:true}:m); // offline: keep the cache, mark stale
      return null;
    }finally{setOrgResolved(true);}
  },[]);
  useEffect(()=>{
    if(!(s.onboarded&&s.profile?.email))return;
    if(pendingJoin){ if(joinStage==="proceed")setJoinStage("redeem"); return; } // redeem first, `me` after
    refreshOrg();
  },[s.onboarded,s.profile?.email,pendingJoin,joinStage,refreshOrg]);
  const activeMe=isActiveOrg(orgMe)?orgMe:null;           // suspended → unbranded, unflagged
  const orgSuspended=isSuspendedOrg(orgMe);
  const suspendAckPrev=useRef(suspendAckKey);
  useEffect(()=>{
    const prev=suspendAckPrev.current;
    if(prev&&prev!==suspendAckKey)LS.del(prev);            // left / removed / switched org
    if(suspendAckKey&&!orgSuspended)LS.del(suspendAckKey);  // active again → show the sheet on the next suspension
    suspendAckPrev.current=suspendAckKey;
    setSuspendedAck(!!(suspendAckKey&&orgSuspended&&LS.get(suspendAckKey)));
  },[suspendAckKey,orgSuspended]);
  const flags=useMemo(()=>flagsFrom(activeMe),[activeMe]);
  useEffect(()=>{setFlagsSnapshot(flags);},[flags]);
  const brandVersion=activeMe?.branding?.version, brandUpdated=activeMe?.branding?.updated_at, orgId=activeMe?.org?.id, orgName=activeMe?.org?.name;
  useEffect(()=>{
    if(pendingJoin)return; // the invite preview owns the branding until the join completes
    const brand=memberBrand(activeMe);
    if(brand)applyBrand(brand);
    else if(V.brand)applyBrand(null);
  },[brandVersion,brandUpdated,orgId,orgName,pendingJoin]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(()=>{applyManifest(activeMe?.branding||null,flags);},[brandVersion,orgId,flags]); // eslint-disable-line react-hooks/exhaustive-deps
  // Realtime: one subscription per app; OrgInbox listens through emitOrgPing. Also refetch on foreground.
  useEffect(()=>{
    if(!activeMe||!s.profile?.email)return;
    const unsub=subscribeOrg(s.profile.email,(p)=>{emitOrgPing(p);refreshOrg();});
    const vis=()=>{if(document.visibilityState==="visible")refreshOrg();};
    document.addEventListener("visibilitychange",vis);
    return()=>{unsub();document.removeEventListener("visibilitychange",vis);};
  },[orgId,s.profile?.email,refreshOrg]); // eslint-disable-line react-hooks/exhaustive-deps
  // Flag gating: a tab reached via hash/shortcut/notification while its flag is off goes home
  useEffect(()=>{
    const f=TAB_FLAGS[s.tab];
    const tab=typeof s.tab==="string"?s.tab:"";
    const blocked=(f&&flags[f]===false)||(flags.social===false&&tab.startsWith("social"))||(tab==="org_inbox"&&!activeMe);
    if(blocked)d({type:"TAB",tab:"home"});
  },[s.tab,flags,activeMe]);
  // ?open-org-inbox=1 / push deep link → inbox once the org is known
  useEffect(()=>{
    if(s.onboarded&&activeMe&&LS.get(OPEN_INBOX_KEY)){LS.del(OPEN_INBOX_KEY);d({type:"TAB",tab:"org_inbox"});}
  },[s.onboarded,activeMe]);
  const finishJoin=useCallback((result)=>{
    // Seed the org from the redeem response so the brand effect does not flash back to
    // IronLog between clearing the pending join and `me` resolving (updates are batched).
    if(result?.org){
      setOrgMe(writeOrgCache({org:result.org,role:result.role,branding:result.branding||null,flags:{},consent:result.consent_defaults||null,
        coach:null,coaches:[],unread:{messages:0,announcements:0},powered_by:result.branding?.powered_by_badge??true}));
    }
    LS.del(PENDING_JOIN_KEY);setPendingJoin(null);setJoinStage(null);setJoinSignIn(false);
    if(result?.org?.name)SuccessToastCtrl.show(`Welcome to ${result.org.name}`);
    refreshOrg();
  },[refreshOrg]);
  // S5: a 403 feature_disabled from a gated endpoint (flag changed under an open screen):
  // one toast with the errorMessage copy, then refresh `me` so the flags hide the tab.
  useEffect(()=>{
    setFeatureDisabledHandler((flag)=>{SuccessToastCtrl.error(errorMessage({code:"feature_disabled",flag}));refreshOrg();});
    return()=>setFeatureDisabledHandler(null);
  },[refreshOrg]);
  const orgCtx=useMemo(()=>({org:activeMe,raw:orgMe,suspended:orgSuspended,stale:!!orgMe?.stale,resolved:orgResolved,
    flags,unread:orgUnread,setUnread:setOrgUnread,refreshOrg}),[activeMe,orgMe,orgSuspended,orgResolved,flags,orgUnread,refreshOrg]);
  const orgBadge=(orgUnread?.messages||0)+(orgUnread?.announcements||0);
  // B9: installed-app icon badge mirrors the inbox bell (unread messages + announcements from `me`)
  useEffect(()=>{
    if(typeof navigator==="undefined"||typeof navigator.setAppBadge!=="function")return;
    try{
      if(orgBadge>0)navigator.setAppBadge(orgBadge).catch(()=>{});
      else if(typeof navigator.clearAppBadge==="function")navigator.clearAppBadge().catch(()=>{});
    }catch{}
  },[orgBadge]);
  // Onboarded + email is enough to attempt a redeem; a dead session surfaces as a 401 → "sign in again"
  const hasAccount=!!(s.onboarded&&s.profile?.email);
  // Join overlays: landing (pre- or post-session), then redeem + consent once a session exists
  const joinOverlay=pendingJoin?(
    joinStage==="landing"?<JoinLanding code={pendingJoin} hasAccount={hasAccount}
        onProceed={(kind)=>{if(kind==="redeem")setJoinStage("redeem");else{setJoinSignIn(kind==="signin");setJoinStage("proceed");}}}
        onCancel={()=>{LS.del(PENDING_JOIN_KEY);setPendingJoin(null);setJoinStage(null);}}/>
    :joinStage==="redeem"&&hasAccount?<JoinRedeem key={pendingJoin} code={pendingJoin} onDone={finishJoin}/>
    :null
  ):null;

  // ─── Widget Data — expose key stats for iOS Shortcuts / Capacitor / home screen widgets ───
  useEffect(()=>{
    if(!s.onboarded)return;
    const streak2=(()=>{let c=0;for(let i=0;i<60;i++){if(s.workouts.some(w=>w.date===ago(i)))c++;else if(i>0)break;}return c;})();
    const todayW=s.workouts.find(w=>w.date===today());
    const todayN=s.nutrition.find(n=>n.date===today());
    const latestBW=s.body[0]?.weight;
    const getBest2=(id)=>{let b=0;s.workouts.forEach(w=>w.exercises.forEach(e=>{if(e.exerciseId===id)e.sets.forEach(st=>{if(st.weight>b)b=st.weight;});}));return b;};
    LS.set("ft-widget",{
      updated:new Date().toISOString(),
      name:s.profile?.firstName||s.profile?.nickname||"",
      streak:streak2,
      todayWorkedOut:!!todayW,
      todayCalories:todayN?.cal||0,
      todayProtein:todayN?.protein||0,
      calGoal:s.goals?.cal||2400,
      proteinGoal:s.goals?.protein||180,
      weight:latestBW||null,
      big3:getBest2("bench")+getBest2("squat")+getBest2("deadlift"),
      totalWorkouts:s.workouts.length,
      phase:phaseTypeId((s.phases||[]).find(p=>phaseIsActive(p))?.type)||null,
    });
  },[s.workouts.length,s.nutrition.length,s.body.length,s.onboarded,s.goals?.protein,s.goals?.cal,s.body?.[0]?.weight,s.phases?.length]);

  // Capture beforeinstallprompt for smart install banner
  useEffect(()=>{
    const handler=(e)=>{e.preventDefault();setInstallPrompt(e);
      LS.set("ft-install-shown",true);
      if(parseInt(LS.get("ft-visits")||"0")>=3&&!LS.get("ft-install-dismissed"))setShowInstallBanner(true);
    };
    window.addEventListener("beforeinstallprompt",handler);
    return()=>window.removeEventListener("beforeinstallprompt",handler);
  },[]);

  // Handle manifest shortcut deep links (hash routing) — on load, and on hashchange so a
  // shortcut still lands when the app is already open (launch_handler focus-existing)
  useEffect(()=>{
    const go=()=>{
      const hash=window.location.hash.replace("#","");
      if(hash&&["log_workout","log_nutrition","log_body","plan_coach","track_readiness","plan_goals"].includes(hash)){
        d({type:"TAB",tab:hash});
        window.location.hash="";
      }
    };
    go();
    window.addEventListener("hashchange",go);
    return()=>window.removeEventListener("hashchange",go);
  },[]);

  // Handle /u/username routes (from 404.html SPA redirect)
  const [publicProfile,setPublicProfile]=useState(null);
  useEffect(()=>{
    const params=new URLSearchParams(window.location.search);
    const route=params.get("route");
    if(route){
      // Clean the URL without reloading
      window.history.replaceState(null,"",window.location.origin+window.location.pathname);
      // Parse /u/username
      const match=route.match(/^\/u\/([a-zA-Z0-9_]+)/);
      if(match){
        const username=match[1].toLowerCase();
        setPublicProfile({username,loading:true});
        SocialAPI.getProfile(username).then(data=>{
          if(data?.found)setPublicProfile({...data.profile,username,loading:false});
          else setPublicProfile({username,loading:false,notFound:true});
        }).catch(()=>setPublicProfile({username,loading:false,notFound:true}));
      }
      // Parse /join/CODE (invite link from 404.html) — same handling as ?join=
      const joinMatch=route.match(/^\/join\/([A-Za-z0-9]+)/);
      if(joinMatch){
        const code=joinMatch[1].toUpperCase().slice(0,16);
        LS.set(PENDING_JOIN_KEY,code);setPendingJoin(code);setJoinStage("landing");
      }
      // Parse /add?code=IRON-XXXX (QR scan landing)
      const addMatch=route.match(/^\/add/);
      if(addMatch){
        // The ?code= param was preserved by 404.html redirect into the route param
        const codeFromRoute=new URLSearchParams(route.split("?")[1]||"").get("code");
        const codeFromSearch=params.get("code");
        // B3 fix: use "" fallback instead of "|" (was harmless but confusing)
        const finalCode=(codeFromRoute||codeFromSearch||"").toUpperCase();
        // Was gated on startsWith("IRON-"), which silently dropped every code the
        // server actually issues — a scanned QR or a shared link did nothing at all.
        // The server resolves any spelling, so anything code-shaped is worth carrying.
        if(finalCode&&/^(IRON[-\s]?)?[A-Z0-9]{4,8}$/.test(finalCode)){
          // B2 fix: don't navigate here — s.onboarded is always false in [] effect.
          // The [s.onboarded] Step 2 effect handles navigation once INIT fires.
          LS.set("ft-pending-add",finalCode);
        }
      }
    }
  },[]);

  // Listen for SW navigation messages (from notification action clicks)
  useEffect(()=>{
    const handler=(e)=>{
      if(e.data?.type==="NAVIGATE"&&e.data.tab){d({type:"TAB",tab:e.data.tab});}
    };
    navigator.serviceWorker?.addEventListener("message",handler);
    return()=>navigator.serviceWorker?.removeEventListener("message",handler);
  },[]);

  useEffect(()=>{
    (async()=>{
      try{
        const data={};
        ["ft-w","ft-n","ft-b","ft-ex","ft-g","ft-sched","ft-active-program","ft-units","ft-onb","ft-photos","ft-profile","ft-checkins","ft-milestones","ft-phases","ft-injuries","ft-syncprefs","ft-a11y","ft-deletions"].forEach(k=>{const v=LS.get(k);if(v!==null)data[k]=v;});
        const localOnboarded=data["ft-onb"]||false;
        // ── SSO: if not onboarded locally but SSO cookie exists, try silent login ──
        if(!localOnboarded&&!data["ft-session-token"]){
          const ssoToken=Cookie.get("ironlog_session");
          if(ssoToken){
            try{
              const vRes=await fetch(`${SYNC_URL}/api/auth/session`,{method:"POST",
                headers:{"Content-Type":"application/json","X-Session-Token":ssoToken},
                body:JSON.stringify({action:"validate"})});
              const vJson=await vRes.json();
              if(vJson.valid&&vJson.email){
                // Valid SSO — pull data from cloud
                const deviceId=LS.get("ft-device-id")||uid();
                LS.set("ft-device-id",deviceId);
                LS.set("ft-session-token",ssoToken);
                LS.set("ft-session-email",vJson.email);
                const pull=await CloudSync.pull(vJson.email,deviceId,null);
                if(pull?.success&&pull?.data){
                  const cloud=pull.data;
                  LS.set("ft-active-program",activeProgramFromWire(cloud));
                  if(cloud.supplements)LS.set("ft-supplements",cloud.supplements);
                  d({type:"INIT",p:{
                    workouts:cloud.workouts||[],nutrition:cloud.nutrition||[],body:cloud.body||[],
                    exercises:cloud.exercises||defaultExercises,goals:cloud.goals||init.goals,
                    schedule:cloud.schedule||defaultSchedule,activeProgram:activeProgramFromWire(cloud),units:cloud.units||"lbs",
                    onboarded:true,photos:cloud.photos||[],profile:cloud.profile||init.profile,
                    checkins:cloud.checkins||[],milestones:cloud.milestones||[],
                    phases:cloud.phases||[],injuries:cloud.injuries||[],
                    syncPrefs:cloud.syncPrefs||init.syncPrefs,a11y:data["ft-a11y"]||init.a11y}});
                  return;
                }
              }
            }catch(e){/* SSO failed silently — fall through to normal boot */}
          }
        }
        const localHealth=hydrateLocalHealth(data);
        d({type:"INIT",p:{workouts:data["ft-w"]||[],nutrition:data["ft-n"]||[],body:data["ft-b"]||[],
          exercises:data["ft-ex"]||defaultExercises,goals:data["ft-g"]||init.goals,schedule:data["ft-sched"]||defaultSchedule,
          activeProgram:normalizeActiveProgram(data["ft-active-program"]),
          units:data["ft-units"]||"lbs",onboarded:localOnboarded,photos:data["ft-photos"]||[],
          profile:data["ft-profile"]||init.profile,checkins:data["ft-checkins"]||[],milestones:data["ft-milestones"]||[],
          phases:localHealth.phases,injuries:localHealth.injuries,
          syncPrefs:data["ft-syncprefs"]||init.syncPrefs,a11y:data["ft-a11y"]||init.a11y,
          deletions:data["ft-deletions"]||init.deletions}});
      }catch(e){
        d({type:"INIT",p:{exercises:defaultExercises,goals:init.goals,schedule:defaultSchedule}});
      }
    })();
  },[]);

  // Skip the FIRST persistence pass after hydration — otherwise we echo the
  // just-pulled cloud snapshot straight back to the server and write it to LS,
  // which races with any in-flight pull and amplifies write traffic.
  const _hydratedRef=useRef(false);
  // Keep a live ref to the current state so debounced/queued callbacks read the latest
  const _latestStateRef=useRef(s);
  useEffect(()=>{_latestStateRef.current=s;},[s]);
  useEffect(()=>{
    if(!s.loaded)return;
    if(!_hydratedRef.current){
      _hydratedRef.current=true;
      return; // first run after INIT — do not persist / push
    }
    (async()=>{try{
      LS.set("ft-w",s.workouts);
      LS.set("ft-n",s.nutrition);
      LS.set("ft-b",s.body);
      LS.set("ft-ex",s.exercises);
      LS.set("ft-g",s.goals);
      LS.set("ft-sched",s.schedule);
      LS.set("ft-active-program",s.activeProgram);
      LS.set("ft-units",s.units);
      LS.set("ft-onb",s.onboarded);
      LS.set("ft-photos",s.photos);
      LS.set("ft-profile",s.profile);
      LS.set("ft-checkins",s.checkins||[]);
      LS.set("ft-milestones",s.milestones||[]);
      LS.set("ft-phases",s.phases||[]);
      LS.set("ft-injuries",s.injuries||[]);
      LS.set("ft-syncprefs",s.syncPrefs||init.syncPrefs);
      // Tombstones must outlive a reload: a delete made offline has to still reach the
      // server on the next successful push, however long that takes.
      LS.set("ft-deletions",s.deletions||init.deletions);
      CloudSync._onDeletionsApplied=(sent)=>d({type:"DELETIONS_SYNCED",sent});
      LS.set("ft-a11y",s.a11y||init.a11y);
      // Auto-sync to cloud (debounced). Pass a getter so the 3s-delayed push
      // sends the latest state, not a stale snapshot captured at debounce time.
      if(s.onboarded&&s.profile?.email){
        CloudSync.debouncedPush(()=>_latestStateRef.current);
      }
    }catch(e){console.warn("Error:",e);}})();
  },[s.workouts,s.nutrition,s.body,s.exercises,s.goals,s.schedule,s.activeProgram,s.units,s.onboarded,s.photos,s.profile,s.checkins,s.milestones,s.phases,s.injuries,s.syncPrefs,s.a11y,s.loaded]);

  // Wire up MsgBannerCtrl ref so sync.js can show banners
  useEffect(()=>{setMsgBannerCtrlRef(MsgBannerCtrl);},[]);

  // Expose sync function for service worker background sync
  useEffect(()=>{
    if(s.loaded&&s.onboarded&&s.profile?.email){
      window.__ironlog_sync=()=>CloudSync.push(_latestStateRef.current);
    }
    return()=>{window.__ironlog_sync=null;};
  },[s.loaded,s.onboarded,s.profile?.email]);

  // Sync on app open — push local data to server, but NOT on the very first
  // load after a cloud pull (the _hydratedRef gate covers that).
  useEffect(()=>{
    if(s.loaded&&s.onboarded&&s.profile?.email&&_hydratedRef.current){
      CloudSync.push(_latestStateRef.current);
    }
  },[s.loaded,s.onboarded,s.profile?.email]);

  // Device storage full: LS.set fires this when a write is refused, so data is no longer
  // being saved on this device. Keep a banner up that leads to Settings → Data.
  const [storageFull,setStorageFull]=useState(false);
  const [settingsJump,setSettingsJump]=useState(null);
  useEffect(()=>{
    const onQuota=()=>setStorageFull(true);
    window.addEventListener("ironlog:quota-exceeded",onQuota);
    return()=>window.removeEventListener("ironlog:quota-exceeded",onQuota);
  },[]);

  // Listen for service worker update
  const [updateAvailable,setUpdateAvailable]=useState(false);
  useEffect(()=>{
    const handler=(e)=>{if(e.data?.type==="UPDATE_AVAILABLE")setUpdateAvailable(true);};
    navigator.serviceWorker?.addEventListener("message",handler);
    return()=>navigator.serviceWorker?.removeEventListener("message",handler);
  },[]);

  // ─── Smart Notification Engine ───
  useEffect(()=>{
    if(!s.loaded||!s.onboarded||!LS.get("ft-reminders"))return;
    const hr=new Date().getHours();
    // Quiet hours
    if(hr>=22||hr<7)return;
    
    const td=today();
    const dow=new Date().getDay();
    const todayType=(s.schedule.overrides||{})[td]||(s.schedule.weekly||{})[dow]||"Rest";
    const hasWorkout=s.workouts.some(w=>w.date===td);
    const hasCheckin=(s.checkins||[]).some(c=>c.date===td);
    const todayNutrition=s.nutrition.find(n=>n.date===td);
    const hasMeals=todayNutrition?.meals?.some(m=>(m.items||[]).length>0);

    // Analyze user patterns for smart timing
    const recentWorkoutHours=s.workouts.slice(0,14).map(w=>{
      // Estimate workout time from date order (rough heuristic)
      return null; // Would need actual timestamps
    }).filter(Boolean);

    // Build notification queue based on time of day + what's missing
    const queue=[];
    const lastNotifKey=`ft-notif-${td}-${hr}`;
    if(LS.get(lastNotifKey))return; // Already notified this hour block

    if(hr>=6&&hr<=9&&!hasMeals){
      queue.push({tag:"meal-breakfast",body:"Good morning! Log your breakfast to stay on track.",
        actions:[{action:"log_nutrition",title:"Log Breakfast"},{action:"dismiss",title:"Later"}]});
    }
    if(hr>=11&&hr<=13&&todayNutrition&&!todayNutrition.meals?.find(m=>m.name==="Lunch"&&(m.items||[]).length>0)){
      queue.push({tag:"meal-lunch",body:"Lunchtime — log what you're eating to hit your macro goals.",
        actions:[{action:"log_nutrition",title:"Log Lunch"},{action:"dismiss",title:"Later"}]});
    }
    // Workout nudge lands in the window the user picked at signup (default mid-afternoon)
    const tt=s.profile?.trainingTime;
    const [wStart,wEnd]=tt==="Morning"?[6,9]:tt==="Midday"?[11,13]:tt==="Evening"?[16,19]:[14,17];
    if(hr>=wStart&&hr<=wEnd&&todayType!=="Rest"&&!hasWorkout){
      queue.push({tag:"workout-reminder",body:`${todayType} day — time to get after it!`,
        actions:[{action:"log_workout",title:"Log Workout"},{action:"dismiss",title:"Skip Today"}]});
    }
    if(hr>=17&&hr<=20&&todayNutrition&&!todayNutrition.meals?.find(m=>m.name==="Dinner"&&(m.items||[]).length>0)){
      queue.push({tag:"meal-dinner",body:"Don't forget to log dinner — you're close to your daily goals.",
        actions:[{action:"log_nutrition",title:"Log Dinner"},{action:"dismiss",title:"Later"}]});
    }
    if(hr>=19&&hr<=21&&!hasCheckin){
      queue.push({tag:"checkin-reminder",body:"Quick 30-sec check-in — how are you feeling today?",
        actions:[{action:"checkin",title:"Check In"},{action:"dismiss",title:"Later"}]});
    }
    if(hr>=13&&hr<=16){
      // B23 fix: also check quick-tap water widget for hydration notification
      const waterFromWidget=parseInt(LS.get("ft-water-"+today()))||0;
      const waterCups=Math.max(todayNutrition?.water||0,waterFromWidget);
      if(waterCups<4){
        queue.push({tag:"hydration",body:`You've logged ${waterCups} cups of water today. Stay hydrated!`,
          actions:[{action:"log_nutrition",title:"Log Water"},{action:"dismiss",title:"Done"}]});
      }
    }

    if(queue.length===0)return;
    LS.set(lastNotifKey,true);

    if("Notification" in window&&Notification.permission==="granted"&&navigator.serviceWorker?.controller){
      navigator.serviceWorker.ready.then(reg=>{
        // Send first queued notification (don't spam)
        const notif=queue[0];
        setTimeout(()=>reg.showNotification(appName(),{
          body:notif.body,icon:"./icon.svg",badge:"./icon.svg",
          tag:notif.tag,vibrate:[100,50,100],
          actions:notif.actions
        }),2000);
      });
    }
  },[s.loaded,s.onboarded]);

  // ─── Web Push Subscription (for server-sent notifications) ───
  useEffect(()=>{
    if(!s.loaded||!s.onboarded||!s.profile?.email)return;
    if(!("serviceWorker" in navigator)||!("PushManager" in window))return;
    if(!LS.get("ft-reminders"))return; // Only subscribe if notifications enabled
    if(LS.get("ft-push-subscribed"))return;

    navigator.serviceWorker.ready.then(async(reg)=>{
      try{
        let sub=await reg.pushManager.getSubscription();
        if(!sub){
          const vapidKey="BMZyIUQO08nuR7afvqNIWBK_ZOcr7PHwT2YwIxTa_ONwAD1YCaJ8Qkb4q4TTYSx_sTN-7-5vCzR8ApNZuyg-Ttc";
          // Convert base64url to standard base64 for atob
          const keyBytes=Uint8Array.from(atob(vapidKey.replace(/-/g,'+').replace(/_/g,'/')),c=>c.charCodeAt(0));
          sub=await reg.pushManager.subscribe({userVisibleOnly:true,applicationServerKey:keyBytes});
        }
        // Send subscription to backend
        const res=await fetch(`${SYNC_URL}/api/push`,{
          method:"POST",
          headers:AuthToken.getHeaders(s.profile.email),
          body:JSON.stringify({action:"subscribe",email:s.profile.email,subscription:sub.toJSON()}),
        });
        if(res.ok)LS.set("ft-push-subscribed",true);
        else console.warn("Push subscribe response:",await res.text());
      }catch(e){console.warn("Web Push subscription failed:",e.message||e);}
    });
  },[s.loaded,s.onboarded]);

  // ─── Interactive Guided Walkthrough ───
  const [guideStep,setGuideStep]=useState(LS.get("ft-guide-done")?-1:0);
  const [guideActive,setGuideActive]=useState(false);
  const [showFeatureHelp,setShowFeatureHelp]=useState(null);
  const [showGlobalCheckin,setShowGlobalCheckin]=useState(false);
  const [showNotifPrompt,setShowNotifPrompt]=useState(false);

  // Activate guide after onboarding completes (check each render)
  useEffect(()=>{
    if(s.onboarded&&!LS.get("ft-guide-done")&&guideStep>=0&&!guideActive)setGuideActive(true);
  },[s.onboarded]);

  const completeGuide=()=>{
    setGuideStep(-1);setGuideActive(false);LS.set("ft-guide-done",true);
    SuccessToastCtrl.show("You're all set! 💪");
    if(!LS.get("ft-notif-prompted"))setTimeout(()=>setShowNotifPrompt(true),600);
  };

  const guideSteps=[
    {id:"welcome",title:`Welcome to ${appName()}!`,msg:"Let's walk through every section of the app. By the end you'll have real data logged and know where everything is.",ic:Icons.zap},
    {id:"workout",title:"Log Your First Workout",msg:"Search for an exercise like Bench Press, add sets with weight and reps. The app auto-suggests progressive overload based on your history.",ic:Icons.dumbbell,
      action:()=>d({type:"TAB",tab:"log_workout"}),hint:"Search for an exercise and add some sets"},
    {id:"nutrition",title:"Track What You Ate",msg:"Search 200+ foods, scan a barcode, or add custom items. Your recent foods show as quick-add chips. Save favorite meals for one-tap logging.",ic:Icons.fork,
      action:()=>d({type:"TAB",tab:"log_nutrition"}),hint:"Search or scan to log your first meal"},
    {id:"body",title:"Record Your Starting Point",msg:"Enter your weight and measurements. These become your baseline — the app shows trend arrows and change over time.",ic:Icons.scale,
      action:()=>d({type:"TAB",tab:"log_body"}),hint:"Enter your weight to set your starting point"},
    {id:"track",title:"Track Your Progress",msg:"Analytics, exercise progression charts, workout duration trends, muscle heat maps, strength scores, PRs page, volume tracking, and weekly summaries — all built from your logged data.",ic:Icons.chart,
      action:()=>d({type:"TAB",tab:"track"}),hint:"Browse your charts and data — it grows as you log"},
    {id:"plan",title:"Your Training Toolkit",msg:"AI Coach gives daily programs. Schedule sets your weekly split. Programs offers PPL, 5/3/1, and more. Plus meal plans, fast food hacks, 1RM calculator, injury tracker, and goal engine.",ic:Icons.calendar,
      action:()=>d({type:"TAB",tab:"plan"}),hint:"Explore your training programs and tools"},
    {id:"social",title:"Connect With Friends",msg:"Add friends by code, username, or email. Compare stats, send messages, join groups with leaderboards and chat. Earn badges and climb challenge tiers.",ic:Icons.users,
      action:()=>d({type:"TAB",tab:"social"}),hint:"Set up your profile and add your first friend"},
    {id:"settings",title:"Customize Your Experience",msg:"Set daily goals, choose units, pick a theme, manage your schedule, edit exercises, and control privacy. Your data syncs automatically to the cloud.",ic:Icons.sliders,
      action:()=>d({type:"TAB",tab:"settings"}),hint:"Set your calorie and protein goals"},
    {id:"done",title:"You're Ready to Train!",msg:"You've seen everything. The app gets smarter as you use it — log consistently, check in daily, and watch your data build. Let's go!",ic:Icons.check},
  ];

  useEffect(()=>{
    if(!guideActive||guideStep<=0||guideStep>=guideSteps.length)return;
    const step=guideSteps[guideStep];
    if(step.action)step.action();
  },[guideStep,guideActive]);

  const handleNotifAccept=async()=>{
    LS.set("ft-notif-prompted",true);
    if(!("Notification" in window)){
      // iOS only exposes Notification / Push inside an installed Home Screen app (16.4+).
      // Previously this silently did nothing and the user never saw the prompt again.
      setShowNotifPrompt(false);
      if(isIOS()&&!isStandalone()){
        SuccessToastCtrl.show(`Add ${appName()} to your Home Screen to enable notifications`);
        setShowInstallGuide(true);
      }else SuccessToastCtrl.show("Notifications aren't supported in this browser");
      return;
    }
    if("Notification" in window){
      const perm=await Notification.requestPermission();
      if(perm==="granted"){
        LS.set("ft-reminders",true);
        SuccessToastCtrl.show("Notifications enabled");
        if("serviceWorker" in navigator&&"PushManager" in window){
          navigator.serviceWorker.ready.then(async(reg)=>{
            try{
              const vk="BMZyIUQO08nuR7afvqNIWBK_ZOcr7PHwT2YwIxTa_ONwAD1YCaJ8Qkb4q4TTYSx_sTN-7-5vCzR8ApNZuyg-Ttc";
              const keyBytes=Uint8Array.from(atob(vk.replace(/-/g,"+").replace(/_/g,"/")),c=>c.charCodeAt(0));
              let sub=await reg.pushManager.getSubscription();
              if(!sub)sub=await reg.pushManager.subscribe({userVisibleOnly:true,applicationServerKey:keyBytes});
              const res=await fetch(`${SYNC_URL}/api/push`,{method:"POST",
                headers:AuthToken.getHeaders(s.profile?.email||""),
                body:JSON.stringify({action:"subscribe",email:s.profile?.email,subscription:sub.toJSON()})});
              if(res.ok){LS.set("ft-push-subscribed",true);}
              // Welcome notification
              reg.showNotification(appName(),{
                body:"You're all set — we'll send smart reminders for workouts, meals, and check-ins.",
                icon:"./icon.svg",badge:"./icon.svg",tag:"welcome"
              });
            }catch(e){console.warn("Push subscribe failed:",e.message||e);}
          });
        }
      }
    }
    setShowNotifPrompt(false);
  };

  const handleNotifDecline=()=>{
    LS.set("ft-notif-prompted",true);
    setShowNotifPrompt(false);
  };

  // Show onboarding if first launch
  if(s.loaded&&!s.onboarded) return <><Onboarding d={d} initialSignIn={joinSignIn}/>{joinOverlay}</>;

  // Back button component for sub-pages
  const SubBack=({to,label})=>(
    <button onClick={()=>d({type:"TAB",tab:to})} style={{background:"none",border:"none",display:"flex",alignItems:"center",
      gap:5,padding:"8px 0 12px",cursor:"pointer",WebkitTapHighlightColor:"transparent"}}>
      {Icons.chevLeft({size:18,color:V.accent})}
      <span style={{fontSize:14,color:V.accent,fontWeight:600}}>{label||"Back"}</span>
    </button>
  );

  // Slim, theme-aware banner pinned above the content (install / update / offline)
  const TopBanner=({icon,color=V.accent,title,sub,action,onAction,onDismiss})=>(
    <div role="status" style={{display:"flex",alignItems:"center",gap:12,padding:"10px 16px",flexShrink:0,
      background:`${color}0f`,borderBottom:`1px solid ${color}26`}}>
      <IconTile icon={icon} color={color} size={34}/>
      <div style={{flex:1,minWidth:0}}>
        <div style={{fontSize:13,fontWeight:700,color:V.text,lineHeight:1.2}}>{title}</div>
        {sub&&<div style={{fontSize:11,color:V.text3,marginTop:2,lineHeight:1.3}}>{sub}</div>}
      </div>
      {action&&<Btn v="small" onClick={onAction} s={{background:color,color:onAccent(),border:"none",boxShadow:`0 4px 14px -6px ${color}80`}}>{action}</Btn>}
      {onDismiss&&<button onClick={onDismiss} aria-label={`Dismiss ${title}`} style={{width:30,height:30,borderRadius:8,background:V.hoverBg,border:"none",
        cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",WebkitTapHighlightColor:"transparent"}}>
        {Icons.x({size:14,color:V.text3})}
      </button>}
    </div>
  );

  // Map tab to parent for bottom nav highlighting
  const tabParent=(t)=>{
    if(t.startsWith("log_"))return"log";
    if(t.startsWith("track_"))return"track";
    if(t.startsWith("plan_"))return"plan";
    if(t.startsWith("social_"))return"social";
    if(t.startsWith("admin_"))return"admin";
    return t;
  };
  const activeParent=tabParent(s.tab);

  const tabs=[
    {id:"home",icon:Icons.activity,label:"Home"},
    {id:"log",icon:Icons.plus,label:"Log"},
    {id:"track",icon:Icons.chart,label:"Track"},
    {id:"plan",icon:Icons.calendar,label:"Plan"},
    ...(flags.social===false?[]:[{id:"social",icon:Icons.target,label:"Social",badge:unreadMsgCount+socialNotifCount}]),
    {id:"settings",icon:Icons.sliders,label:"Settings"},
    ...(isAdmin?[{id:"admin",icon:Icons.zap,label:"Admin"}]:[]),
  ];

  const a11y=s.a11y||{};

  return(
    <OrgContext.Provider value={orgCtx}>
    <div style={{
      height:"100%",maxWidth:showSidebar?"none":430,margin:"0 auto",
      // Ambient color washes layered over the base background give the app depth
      background:a11y.highContrast?"#000":`${V.ambient}, ${V.bg}`,
      color:a11y.highContrast?"#fff":V.text,fontFamily:V.font,
      display:"flex",flexDirection:"column",position:"relative",
      fontSize:a11y.largeText?"17px":"16px",
      marginLeft:showSidebar?sidebarWidth:undefined,
      ...(a11y.reduceMotion?{transition:"none"}:{}),
    }}>

      {/* Persistent Sidebar Navigation — tablet & desktop */}
      {showSidebar&&(
        <nav aria-label="Primary navigation" style={{
          position:"fixed",left:0,top:0,bottom:0,width:sidebarWidth,background:V.navBg,
          backdropFilter:"blur(24px) saturate(180%)",borderRight:`1px solid ${V.divider}`,
          display:"flex",flexDirection:"column",padding:isDesktop?"24px 0":"18px 0",zIndex:100,overflowY:"auto",
        }}>
          <button onClick={()=>d({type:"TAB",tab:"home"})} aria-label="Home"
            title={!isDesktop?appName():undefined}
            style={{display:"flex",alignItems:"center",gap:10,
              justifyContent:isDesktop?"flex-start":"center",
              background:"none",border:"none",cursor:"pointer",
              padding:isDesktop?"12px 26px":"8px 0",marginBottom:isDesktop?20:14,
              WebkitTapHighlightColor:"transparent"}}>
            <LogoMark size={34}/>
            {isDesktop&&<BrandTitle size={18}/>}
          </button>
          {tabs.map(t=>{
            const active=activeParent===t.id;
            return(
              <button key={t.id} onClick={()=>{d({type:"TAB",tab:t.id});}}
                aria-label={t.label} aria-current={active?"page":undefined}
                title={!isDesktop?t.label:undefined}
                className="nav-item"
                style={{
                  display:"flex",alignItems:"center",gap:12,
                  padding:isDesktop?"10px 16px":"12px 0",
                  margin:isDesktop?"1px 10px":"1px 6px",
                  justifyContent:isDesktop?"flex-start":"center",
                  background:active?`linear-gradient(90deg,${V.accent}16,${V.accent}08)`:"transparent",
                  border:`1px solid ${active?`${V.accent}22`:"transparent"}`,
                  borderRadius:12,cursor:"pointer",width:isDesktop?"calc(100% - 20px)":"calc(100% - 12px)",
                  boxShadow:active?`0 4px 14px -6px ${V.accent}35`:"none",
                  WebkitTapHighlightColor:"transparent",transition:"all .15s",position:"relative",
                }}>
                <div style={{width:22,display:"flex",justifyContent:"center",flexShrink:0}}>
                  {t.id==="log"?(
                    <div style={{width:22,height:22,borderRadius:"50%",
                      background:active?V.accent:"rgba(255,255,255,0.12)",
                      display:"flex",alignItems:"center",justifyContent:"center"}}>
                      {Icons.plus({size:12,color:active?V.bg:V.text3,strokeWidth:3})}
                    </div>
                  ):(
                    t.icon({size:20,color:active?V.accent:V.text3,strokeWidth:active?2.2:1.6})
                  )}
                </div>
                {isDesktop&&(
                  <span style={{fontSize:13,fontWeight:active?700:500,color:active?V.accent:V.text2,
                    fontFamily:V.font}}>{t.label}</span>
                )}
                {t.badge>0&&(
                  isDesktop?(
                    <div style={{marginLeft:"auto",minWidth:18,height:18,borderRadius:9,
                      background:V.danger,display:"flex",alignItems:"center",justifyContent:"center",padding:"0 5px"}}>
                      <span style={{fontSize:9,fontWeight:800,color:"#fff",fontFamily:V.mono}}>{t.badge>9?"9+":t.badge}</span>
                    </div>
                  ):(
                    <div style={{position:"absolute",top:6,right:12,minWidth:16,height:16,borderRadius:8,
                      background:V.danger,display:"flex",alignItems:"center",justifyContent:"center",padding:"0 4px",
                      border:`2px solid ${V.navBg}`}}>
                      <span style={{fontSize:8,fontWeight:800,color:"#fff",fontFamily:V.mono}}>{t.badge>9?"9+":t.badge}</span>
                    </div>
                  )
                )}
              </button>
            );
          })}
          {activeMe?.powered_by&&<PoweredBy inSidebar/>}
        </nav>
      )}

      {/* Status Bar Area */}
      <div style={{height:"env(safe-area-inset-top, 0px)",background:V.bg,flexShrink:0}}/>

      {/* Header */}
      <div style={{
        padding:isDesktop?"16px 40px 12px":showSidebar?"14px 28px 10px":"12px 20px 10px",
        display:"flex",alignItems:"center",
        justifyContent:showSidebar?"flex-end":"space-between",
        gap:12,flexShrink:0,
        maxWidth:contentMaxWidth||undefined,
        width:"100%",margin:showSidebar?"0 auto":undefined,
      }}>
        {!showSidebar&&<button onClick={()=>d({type:"TAB",tab:"home"})} style={{display:"flex",alignItems:"center",gap:8,
          background:"none",border:"none",cursor:"pointer",padding:0,WebkitTapHighlightColor:"transparent"}}>
          <LogoMark size={28}/>
          <BrandTitle size={17}/>
        </button>}
        <div style={{display:"flex",gap:6,alignItems:"center"}}>
          {/* Gym inbox bell — only for org members */}
          {activeMe&&flags.messages!==false&&(
            <button onClick={()=>d({type:"TAB",tab:"org_inbox"})} aria-label={orgBadge>0?`Inbox, ${orgBadge} unread`:"Inbox"} data-testid="org-bell"
              style={{position:"relative",width:28,height:28,borderRadius:8,background:s.tab==="org_inbox"?`${V.accent}1c`:V.inputBg,
                border:`1px solid ${s.tab==="org_inbox"?V.accent+"40":V.cardBorder}`,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",
                WebkitTapHighlightColor:"transparent"}}>
              {Icons.bell({size:14,color:s.tab==="org_inbox"?V.accent:V.text2,strokeWidth:2})}
              {orgBadge>0&&(
                <span data-testid="org-bell-badge" style={{position:"absolute",top:-5,right:-5,minWidth:16,height:16,borderRadius:8,background:V.danger,
                  border:`2px solid ${V.bg}`,display:"flex",alignItems:"center",justifyContent:"center",paddingLeft:3,paddingRight:3,
                  fontSize:8,fontWeight:800,color:"#fff",fontFamily:V.mono,boxSizing:"content-box",lineHeight:1}}>{orgBadge>9?"9+":orgBadge}</span>
              )}
            </button>
          )}
          {/* Daily check-in chip */}
          {(()=>{const ciDone=(s.checkins||[]).some(c=>c.date===today());return(
            <button onClick={()=>setShowGlobalCheckin(true)} style={{
              display:"flex",alignItems:"center",gap:4,padding:"5px 9px",borderRadius:8,
              border:`1px solid ${ciDone?V.accent+"30":V.accent+"60"}`,
              background:ciDone?`${V.accent}08`:`${V.accent}14`,
              cursor:"pointer",WebkitTapHighlightColor:"transparent",
              animation:ciDone?"none":"checkinPulse 2.5s ease-in-out infinite",
            }}>
              <span style={{fontSize:11}}>{ciDone?"✅":"🫀"}</span>
              <span style={{fontSize:9,fontWeight:700,color:V.accent,letterSpacing:".03em"}}>{ciDone?"Done":"Check In"}</span>
            </button>
          );})()}
          <button onClick={()=>setShowFeatureHelp(activeParent)} aria-label="Show help"
            style={{width:28,height:28,borderRadius:8,background:V.inputBg,
              border:`1px solid ${V.cardBorder}`,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center"}}>
            <span style={{fontSize:12,fontWeight:700,color:V.text3}}>?</span>
          </button>
          <div style={{display:"flex",gap:2,padding:2,borderRadius:10,background:V.hoverBg,
            border:`1px solid ${V.divider}`}}>
          {[7,14,30].map(n=>(
            <button key={n} onClick={()=>d({type:"RANGE",d:n})} style={{
              padding:"4px 9px",borderRadius:8,border:"none",fontSize:10,fontWeight:700,fontFamily:V.font,
              background:s.range===n?`${V.accent}1c`:"transparent",color:s.range===n?V.accent:V.text3,cursor:"pointer",
              boxShadow:s.range===n?`0 1px 6px -1px ${V.accent}40`:"none",
              WebkitTapHighlightColor:"transparent",
            }}>{n}d</button>
          ))}
          </div>
        </div>
      </div>

      {storageFull&&(
        <TopBanner icon={Icons.trash} color={V.danger} title="Storage full — changes aren't being saved"
          sub="Export a backup, then remove old photos or data to free up space" action="Free up space"
          onAction={()=>{setSettingsJump({id:"data",t:Date.now()});d({type:"TAB",tab:"settings"});}}
          onDismiss={()=>setStorageFull(false)}/>
      )}

      {/* Update available banner */}
      {updateAvailable&&(
        <TopBanner icon={Icons.refresh} title="New version ready" sub={`Reload to get the latest ${appName()}`} action="Update"
          onAction={()=>{navigator.serviceWorker?.controller?.postMessage({type:"SKIP_WAITING"});window.location.reload();}}/>
      )}

      {/* Install app banner */}
      {showInstallBanner&&installPrompt&&(!s.onboarded||orgResolved)&&flags.install_banner!==false&&(
        <TopBanner icon={Icons.download} title={`Install ${appName()}`} sub="Add to your home screen for the full app experience" action="Install"
          onAction={async()=>{
            installPrompt.prompt();
            const result=await installPrompt.userChoice;
            if(result.outcome==="accepted")LS.set("ft-installed",true);
            setShowInstallBanner(false);setInstallPrompt(null);
          }}
          onDismiss={()=>{setShowInstallBanner(false);LS.set("ft-install-dismissed",true);}}/>
      )}
      {/* #4: iOS install guide (no install prompt on iOS) */}
      {!installPrompt&&!LS.get("ft-installed")&&!LS.get("ft-install-dismissed")&&isIOS()&&!isStandalone()&&s.onboarded&&s.workouts.length>=2&&flags.install_banner!==false&&(
        <TopBanner icon={Icons.download} title="Add to Home Screen" sub="Offline access, reminders and a full-screen app" action="How"
          onAction={()=>setShowInstallGuide(true)}
          onDismiss={()=>{LS.set("ft-install-dismissed",true);bumpBanner();}}/>
      )}

      {/* White-label: iOS freezes the home-screen icon and name at "Add to Home Screen" time, so a member
          who installed IronLog before joining keeps the old icon until they delete and re-add the app. */}
      {activeMe&&isStandalone()&&!LS.get("ft-org-installed-v"+(activeMe.branding?.version||0))&&(
        <TopBanner icon={Icons.refresh} color={V.accent2} title={`Reinstall to get the ${appName()} icon`}
          sub="Delete the home-screen app and add it again from Safari — your data stays"
          onDismiss={()=>{LS.set("ft-org-installed-v"+(activeMe.branding?.version||0),true);bumpBanner();}}/>
      )}

      {/* White-label: kill switch (503 org_features_disabled) — cached org/brand kept, member told why gym features are off */}
      {orgNotice&&orgNotice!=="dismissed"&&s.onboarded&&(
        <TopBanner icon={Icons.pause} color={V.warn} title="Gym features paused" sub={errorMessage(orgNotice)}
          onDismiss={()=>setOrgNotice("dismissed")}/>
      )}

      {/* Email verification re-prompt — the reader for the "skipped" state that
          Onboarding writes. Self-gating: renders nothing unless the account
          actually skipped and the backoff window has elapsed. */}
      {s.onboarded&&<EmailVerifyPrompt email={s.profile?.email} TopBanner={TopBanner}/>}

      {/* Update available banner */}
      {showUpdateBanner&&updateVersion&&(
        <TopBanner icon={Icons.zap} color={V.warn} title={`Update available · v${updateVersion}`} sub="Tap Update to see what's new and refresh" action="Update"
          onAction={()=>setShowUpdateGuide(true)}
          onDismiss={()=>{setShowUpdateBanner(false);LS.set("ft-update-dismissed",updateVersion);}}/>
      )}

      {/* Update walkthrough guide */}
      {showUpdateGuide&&(
        <div style={{position:"fixed",top:0,left:0,right:0,bottom:0,zIndex:2000,display:"flex",alignItems:"center",justifyContent:"center",padding:20}}>
          <div style={{position:"absolute",top:0,left:0,right:0,bottom:0,background:"rgba(0,0,0,.7)",backdropFilter:"blur(6px)",animation:"fadeIn .2s ease-out"}} onClick={()=>setShowUpdateGuide(false)}/>
          <div role="dialog" aria-modal="true" aria-label={`Update ${appName()}`} style={{position:"relative",background:V.sheetBg,borderRadius:22,
            padding:24,maxWidth:380,width:"100%",border:`1px solid ${V.cardBorder}`,boxShadow:V.shadowLg,maxHeight:"85vh",overflowY:"auto",
            animation:"popIn .25s cubic-bezier(.2,.8,.3,1) both"}}>
            <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:16}}>
              <LogoMark size={44}/>
              <div style={{minWidth:0}}>
                <div style={{fontSize:17,fontWeight:800,color:V.text,letterSpacing:"-0.01em"}}>{appName()} v{updateVersion}</div>
                <div style={{fontSize:12,color:V.text3,marginTop:2}}>A new version is ready · you're on v{APP_VERSION}</div>
              </div>
            </div>
            {updateNotes&&<Callout icon={Icons.zap} title="What's new" style={{marginBottom:16}}>{updateNotes}</Callout>}
            <div style={{fontSize:11,fontWeight:700,letterSpacing:".12em",textTransform:"uppercase",color:V.warn,marginBottom:12}}>Before updating</div>
            <div style={{display:"flex",flexDirection:"column",gap:14}}>
              <NumberedStep n={1} title="Back up your data" desc="Settings → Backup & Restore → Export JSON saves all your workouts, nutrition and measurements."/>
              <div style={{marginLeft:42,marginTop:-6}}>
                <LinkBtn onClick={()=>{d({type:"TAB",tab:"settings"});setShowUpdateGuide(false);}} style={{padding:0}}>Go to Settings →</LinkBtn>
              </div>
              <NumberedStep n={2} title="Cloud sync is automatic" desc="Your data is already synced. After updating it restores automatically when you sign in with the same email and PIN."/>
              <NumberedStep n={3} title="Refresh the app" desc={isIOS()
                ?`Close ${appName()} completely (swipe up from the app switcher), then reopen it from your home screen.`
                :"Pull down to refresh, or close and reopen the app. In Chrome you can also clear site data to force the update."}/>
              <NumberedStep n={4} title="Restore if needed" desc="If anything looks missing, go to Settings → Backup & Restore → Restore from Cloud."/>
            </div>
            <div style={{display:"flex",gap:8,marginTop:20}}>
              <Btn v="secondary" full onClick={()=>setShowUpdateGuide(false)}>Later</Btn>
              <Btn full onClick={()=>{
                LS.set("ft-update-dismissed",updateVersion);
                // Force reload to get new service worker
                if('serviceWorker' in navigator){
                  navigator.serviceWorker.getRegistration().then(reg=>{if(reg)reg.update();});
                }
                window.location.reload();
              }} s={{background:V.warn,color:onAccent()}}>Update Now</Btn>
            </div>
          </div>
        </div>
      )}

      {/* #1: Offline indicator */}
      {!isOnline&&(
        <div style={{display:"flex",alignItems:"center",justifyContent:"center",gap:6,padding:"6px 12px",
          background:`${V.danger}12`,borderBottom:`1px solid ${V.danger}25`}}>
          <div style={{width:6,height:6,borderRadius:3,background:V.danger,animation:"pulse 2s infinite"}}/>
          <span style={{fontSize:10,fontWeight:600,color:V.danger}}>Offline — changes will sync when reconnected</span>
        </div>
      )}

      {/* Content — scrollable region */}
      <div ref={contentRef} role="main" aria-label="App content"
        {...(!showSidebar?{onTouchStart:handleTouchStart,onTouchEnd:handleTouchEnd}:{})}
        style={{flex:1,minHeight:0,overflowY:"auto",overflowX:"hidden",
        WebkitOverflowScrolling:"touch",
        padding:isDesktop?"0 40px 32px":showSidebar?"0 28px 28px":"0 16px 24px",
        overscrollBehavior:"contain"}}>
        <div key={s.tab} className={a11y.reduceMotion?undefined:"tab-anim"}
          style={{maxWidth:contentMaxWidth||undefined,margin:showSidebar?"0 auto":undefined,width:"100%"}}>
        {s.tab==="home"&&<HomeTab s={s} d={d}/>}
        {s.tab==="log"&&<LogHub s={s} d={d}/>}
        {s.tab==="track"&&<TrackHub s={s} d={d}/>}
        {s.tab==="plan"&&<PlanHub s={s} d={d}/>}
        {s.tab==="settings"&&<SettingsTab s={s} d={d} isAdmin={isAdmin} jump={settingsJump}/>}

        {/* Log sub-pages */}
        {s.tab==="log_workout"&&<div><SubBack to="log" label="Log"/><WorkoutTab s={s} d={d}/></div>}
        {s.tab==="log_nutrition"&&<div><SubBack to="log" label="Log"/><NutritionTab s={s} d={d}/></div>}
        {s.tab==="log_body"&&<div><SubBack to="log" label="Log"/><BodyTab s={s} d={d}/></div>}

        {/* Track sub-pages */}
        {s.tab==="track_calendar"&&<div><SubBack to="track" label="Track"/><CalendarTab s={s} d={d}/></div>}
        {s.tab==="track_nutrition"&&<div><SubBack to="track" label="Track"/><NutritionTab s={s} d={d}/></div>}
        {s.tab==="track_body"&&<div><SubBack to="track" label="Track"/><BodyTab s={s} d={d}/></div>}
        {s.tab==="track_photos"&&<div><SubBack to="track" label="Track"/><ProgressPhotos s={s} d={d}/></div>}
        {s.tab==="track_analytics"&&<div><SubBack to="track" label="Track"/><AnalyticsTab s={s} d={d}/></div>}
        {s.tab==="track_readiness"&&<div><SubBack to="track" label="Track"/><ReadinessTrend s={s}/></div>}
        {s.tab==="track_muscles"&&<div><SubBack to="track" label="Track"/><MuscleHeatMap s={s}/></div>}
        {s.tab==="track_volume"&&<div><SubBack to="track" label="Track"/><VolumeTracker s={s} d={d}/></div>}
        {s.tab==="track_exercise_chart"&&<div><SubBack to="track" label="Track"/><ExerciseChart s={s} d={d}/></div>}
        {s.tab==="track_duration"&&<div><SubBack to="track" label="Track"/><DurationTrends s={s} d={d}/></div>}
        {s.tab==="track_strength"&&<div><SubBack to="track" label="Track"/><StrengthScoreCard s={s}/></div>}
        {s.tab==="track_dataguard"&&<div><SubBack to="track" label="Track"/><DataGuardTab s={s} d={d}/></div>}
        {s.tab==="track_summary"&&<div><SubBack to="track" label="Track"/><WeeklySummary s={s}/></div>}
        {s.tab==="track_compare"&&<div><SubBack to="track" label="Track"/><PhotoCompare s={s}/></div>}
        {s.tab==="track_prs"&&<div><SubBack to="track" label="Track"/><PersonalRecords s={s} d={d}/></div>}

        {/* Plan sub-pages */}
        {s.tab==="plan_coach"&&<div><SubBack to="plan" label="Plan"/><AdaptiveCoach s={s} d={d}/></div>}
        {s.tab==="plan_programs"&&<div><SubBack to="plan" label="Plan"/><ProgramMarketplace s={s} d={d}/></div>}
        {s.tab==="plan_schedule"&&<div><SubBack to="plan" label="Plan"/><CalendarTab s={s} d={d}/></div>}
        {s.tab==="plan_phases"&&<div><SubBack to="plan" label="Plan"/><PhaseTracker s={s} d={d}/></div>}
        {s.tab==="plan_mealplan"&&<div><SubBack to="plan" label="Plan"/><MealPlanGenerator s={s} d={d}/></div>}
        {s.tab==="plan_fastfood"&&<div><SubBack to="plan" label="Plan"/><FastFoodHacks s={s} d={d}/></div>}
        {s.tab==="plan_supplements"&&<div><SubBack to="plan" label="Plan"/><SupplementTracker s={s} d={d}/></div>}
        {s.tab==="plan_goals"&&<div><SubBack to="plan" label="Plan"/><GoalEngine s={s} d={d}/></div>}
        {s.tab==="plan_onerm"&&<div><SubBack to="plan" label="Plan"/><OneRMCalc units={s.units}/></div>}
        {s.tab==="plan_injuries"&&<div><SubBack to="plan" label="Plan"/><InjuryManager s={s} d={d}/></div>}
        {s.tab==="plan_subs"&&<div><SubBack to="plan" label="Plan"/><SubstitutionFinder s={s}/></div>}
        {s.tab==="plan_formcheck"&&<div><SubBack to="plan" label="Plan"/><FormCheckTab s={s} d={d}/></div>}
        {s.tab==="plan_dataguard"&&<div><SubBack to="track" label="Track"/><DataGuardTab s={s} d={d}/></div>}
        {s.tab==="plan_aichat"&&<div><SubBack to="plan" label="Plan"/><AICoachChat s={s}/></div>}
        {s.tab==="admin_panel"&&isAdmin&&<div><SubBack to="admin" label="Admin"/><AdminPanel s={s} initialView="dashboard" isSuperAdmin={isSuperAdmin} adminEmailConfigured={adminEmailConfigured}/></div>}
        {s.tab==="admin_dashboard"&&isAdmin&&<div><SubBack to="admin" label="Admin"/><AdminPanel s={s} initialView="dashboard" isSuperAdmin={isSuperAdmin} adminEmailConfigured={adminEmailConfigured}/></div>}
        {s.tab==="admin_users"&&isAdmin&&<div><SubBack to="admin" label="Admin"/><AdminPanel s={s} initialView="users" isSuperAdmin={isSuperAdmin} adminEmailConfigured={adminEmailConfigured}/></div>}
        {s.tab==="admin_lookup"&&isAdmin&&<div><SubBack to="admin" label="Admin"/><AdminPanel s={s} initialView="lookup" isSuperAdmin={isSuperAdmin} adminEmailConfigured={adminEmailConfigured}/></div>}
        {s.tab==="admin_moderation"&&isAdmin&&<div><SubBack to="admin" label="Admin"/><AdminPanel s={s} initialView="moderation" isSuperAdmin={isSuperAdmin} adminEmailConfigured={adminEmailConfigured}/></div>}
        {s.tab==="admin_health"&&isAdmin&&<div><SubBack to="admin" label="Admin"/><AdminPanel s={s} initialView="health" isSuperAdmin={isSuperAdmin} adminEmailConfigured={adminEmailConfigured}/></div>}
        {s.tab==="admin_push"&&isAdmin&&<div><SubBack to="admin" label="Admin"/><AdminPush s={s} d={d}/></div>}
        {s.tab==="admin_xp"&&isAdmin&&<div><SubBack to="admin" label="Admin"/><AdminXPManager s={s}/></div>}
        {s.tab==="admin_biz"&&isAdmin&&<div><SubBack to="admin" label="Admin"/><AdminBusinessValue s={s} isSuperAdmin={isSuperAdmin} adminEmailConfigured={adminEmailConfigured}/></div>}
        {s.tab==="admin_audit"&&isAdmin&&<div><SubBack to="admin" label="Admin"/><AdminUserAudit s={s}/></div>}
        {s.tab==="admin_orgs"&&isAdmin&&<div><SubBack to="admin" label="Admin"/><AdminOrgs s={s} isSuperAdmin={isSuperAdmin} adminEmailConfigured={adminEmailConfigured}/></div>}
        {s.tab==="admin_org_analytics"&&isAdmin&&<div><SubBack to="admin" label="Admin"/><OrgAnalytics s={s} isSuperAdmin={isSuperAdmin} adminEmailConfigured={adminEmailConfigured}/></div>}

        {/* White-label: gym inbox (announcements + coach messages) */}
        {/* OrgInbox owns its Home back row: the thread view is sized to --content-h and must not sit under a SubBack row */}
        {s.tab==="org_inbox"&&activeMe&&<OrgInbox s={s} d={d}/>}

        {s.tab==="admin"&&isAdmin&&<AdminHub s={s} d={d} isSuperAdmin={isSuperAdmin} adminEmailConfigured={adminEmailConfigured}/>}

        {/* Social tab + sub-pages */}
        {s.tab==="social"&&<SocialTab s={s} d={d} unreadMsgCount={unreadMsgCount}/>}
        {s.tab==="social_feed"&&<div><SubBack to="social" label="Social"/><SocialFeed s={s} d={d}/></div>}
        {s.tab==="social_messages"&&<div><MessagesTab s={s} d={d}/></div>}
        {s.tab==="social_friends"&&<div><SubBack to="social" label="Social"/><SocialFriends s={s} d={d}/></div>}
        {s.tab==="social_groups"&&<div><SubBack to="social" label="Social"/><SocialGroups s={s} d={d}/></div>}
        {s.tab==="social_challenges"&&<div><SubBack to="social" label="Social"/><SocialChallenges s={s} d={d}/></div>}
        {s.tab==="social_profile"&&<div><SubBack to="social" label="Social"/><SocialProfile s={s} d={d}/></div>}
        {s.tab==="social_badges"&&<div><SubBack to="social" label="Social"/><SocialBadges s={s}/></div>}
        {s.tab==="social_notifications"&&<div><SubBack to="social" label="Social"/><SocialNotifications s={s} d={d}/></div>}
        {s.tab==="social_compare"&&<div><SubBack to="social" label="Social"/><SocialCompare s={s}/></div>}
        {s.tab==="social_leaderboard"&&<div><SubBack to="social" label="Social"/><SocialLeaderboard s={s} d={d}/></div>}
        {s.tab==="social_league"&&<div><SubBack to="social" label="Social"/><SocialLeague s={s} d={d}/></div>}
        {s.tab==="social_ironscore"&&<div><SubBack to="social" label="Social"/><SocialIronScore s={s} d={d}/></div>}
        {s.tab==="social_duels"&&<div><SubBack to="social" label="Social"/><SocialDuels s={s} d={d}/></div>}
        {s.tab==="social_wars"&&<div><SubBack to="social" label="Social"/><SocialWeeklyWar s={s} d={d}/></div>}
        {s.tab==="social_rivals"&&<div><SubBack to="social" label="Social"/><SocialRivals s={s} d={d}/></div>}

        {/* Global footer on every page */}

      {/* Public Profile Overlay — shown when someone opens /u/username */}
      {publicProfile&&!publicProfile.loading&&(
        <div style={{position:"fixed",top:0,left:0,right:0,bottom:0,zIndex:2100,display:"flex",alignItems:"center",justifyContent:"center",padding:20}}>
          <div style={{position:"absolute",top:0,left:0,right:0,bottom:0,background:"rgba(0,0,0,.9)",backdropFilter:"blur(8px)"}} onClick={()=>setPublicProfile(null)}/>
          <div style={{position:"relative",background:V.sheetBg,borderRadius:20,
            padding:24,maxWidth:360,width:"100%",border:`1px solid ${V.accent}25`,maxHeight:"85vh",overflowY:"auto"}}>
            {publicProfile.notFound?(
              <div style={{textAlign:"center"}}>
                <div style={{fontSize:32,marginBottom:8}}>🔍</div>
                <div style={{fontSize:16,fontWeight:800,color:V.text}}>User Not Found</div>
                <div style={{fontSize:12,color:V.text3,marginTop:4}}>@{publicProfile.username} doesn't exist or hasn't set up their profile.</div>
                <Btn full onClick={()=>setPublicProfile(null)} s={{marginTop:16}}>Close</Btn>
              </div>
            ):(
              <div style={{textAlign:"center"}}>
                {/* Avatar */}
                <div style={{width:64,height:64,borderRadius:18,background:`linear-gradient(135deg,${V.accent},#ec4899)`,
                  display:"inline-flex",alignItems:"center",justifyContent:"center",marginBottom:12}}>
                  <span style={{fontSize:28,color:V.bg,fontWeight:900}}>{publicProfile.username?.[0]?.toUpperCase()}</span>
                </div>
                <div style={{fontSize:20,fontWeight:800,color:V.text}}>{publicProfile.name||publicProfile.username}</div>
                <div style={{fontSize:12,color:V.text3}}>@{publicProfile.username}</div>
                {publicProfile.bio&&<div style={{fontSize:11,color:V.text2,marginTop:6,lineHeight:1.5}}>{publicProfile.bio}</div>}

                {/* Badges */}
                {publicProfile.badges?.length>0&&(
                  <div style={{display:"flex",flexWrap:"wrap",gap:4,justifyContent:"center",margin:"12px 0"}}>
                    {publicProfile.badges.map((b,i)=><span key={i} style={{padding:"3px 8px",borderRadius:6,
                      background:`${V.accent}08`,border:`1px solid ${V.accent}20`,fontSize:10,color:V.accent,fontWeight:600}}>{b}</span>)}
                  </div>
                )}

                {/* Challenge stats */}
                {publicProfile.challenges?.length>0&&(
                  <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:6,margin:"12px 0"}}>
                    {publicProfile.challenges.map((c,i)=>(
                      <div key={i} style={{padding:"8px 4px",borderRadius:8,background:V.card}}>
                        <div style={{fontSize:14,fontWeight:800,color:V.accent,fontFamily:V.mono}}>{c.value}</div>
                        <div style={{fontSize:8,color:V.text3,textTransform:"uppercase"}}>{c.challenge_id}</div>
                        {c.tier&&<div style={{fontSize:8,color:V.warn}}>{c.tier}</div>}
                      </div>
                    ))}
                  </div>
                )}

                {/* Public stats */}
                {publicProfile.stats?.workoutCount&&(
                  <div style={{fontSize:11,color:V.text3,marginTop:4}}>{publicProfile.stats.workoutCount} workouts logged</div>
                )}

                <div style={{fontSize:10,color:V.text3,marginTop:8}}>Member since {fmtShort(publicProfile.joined?.split("T")[0])}</div>

                {/* Actions */}
                <div style={{display:"flex",gap:8,marginTop:16}}>
                  <Btn v="secondary" full onClick={()=>setPublicProfile(null)}>Close</Btn>
                  {s.profile?.email?(
                    <Btn full onClick={async()=>{
                      const r=await SocialAPI.sendRequest(s.profile.email,publicProfile.username+"@ironlog.space");
                      if(r?.success)SuccessToastCtrl.show("Friend request sent!");
                      else SuccessToastCtrl.error(r?.error||"Could not send request");
                    }}>Add Friend</Btn>
                  ):(
                    <Btn full onClick={()=>{setPublicProfile(null);/* They need to sign up first */}}>Join {appName()}</Btn>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
      {publicProfile?.loading&&(
        <div style={{position:"fixed",top:0,left:0,right:0,bottom:0,zIndex:2100,display:"flex",alignItems:"center",justifyContent:"center",
          background:"rgba(0,0,0,.9)"}}>
          <div style={{textAlign:"center",color:V.text}}>
            <div style={{width:24,height:24,borderRadius:12,border:`2px solid ${V.accent}`,borderTopColor:"transparent",
              animation:"spin 0.8s linear infinite",margin:"0 auto 12px"}}/>
            <div style={{fontSize:12}}>Loading @{publicProfile.username}...</div>
          </div>
        </div>
      )}
        <Footer/>
        </div>
      </div>

      {/* Bottom Nav — mobile only (sidebar replaces it on tablet+) */}
      {!showSidebar&&<nav role="navigation" aria-label="Main navigation" style={{
        flexShrink:0,background:V.navBg,backdropFilter:"blur(24px) saturate(180%)",
        borderTop:`1px solid ${V.divider}`,
        paddingBottom:activeMe?.powered_by?8:"max(8px, env(safe-area-inset-bottom, 8px))",
        display:"flex",justifyContent:"space-around",alignItems:"center",paddingTop:8,
        position:"relative",zIndex:50,
      }}>
        {tabs.map(t=>{
          const active=activeParent===t.id;
          return(
            <button key={t.id} onClick={()=>{Haptic.light();d({type:"TAB",tab:t.id});}} aria-label={t.label} aria-current={active?"page":undefined} style={{
              display:"flex",flexDirection:"column",alignItems:"center",gap:3,
              background:"none",border:"none",cursor:"pointer",
              padding:"6px 8px",minWidth:0,flex:1,
              WebkitTapHighlightColor:"transparent",transition:"all .15s",
            }}>
              <div style={{
                width:active?42:36,height:active?42:36,borderRadius:active?14:12,position:"relative",
                background:active?`linear-gradient(135deg,${V.accent}22,${V.accent2}14)`:"transparent",
                border:`1px solid ${active?`${V.accent}30`:"transparent"}`,
                boxShadow:active?`0 4px 14px -4px ${V.accent}45`:"none",
                display:"flex",alignItems:"center",justifyContent:"center",
                transition:"all .2s ease",
              }}>
                {t.id==="log"?(
                  <div style={{width:active?26:22,height:active?26:22,borderRadius:"50%",
                    background:active?`linear-gradient(135deg,${V.accent},${V.accent2})`:V.mode==="dark"?"rgba(255,255,255,0.12)":"rgba(15,23,42,0.12)",
                    boxShadow:active?`0 2px 10px -2px ${V.accent}70`:"none",
                    display:"flex",alignItems:"center",justifyContent:"center",transition:"all .2s"}}>
                    {Icons.plus({size:active?14:12,color:active?V.bg:V.text3,strokeWidth:3})}
                  </div>
                ):(
                  t.icon({size:active?22:20,color:active?V.accent:V.text3,strokeWidth:active?2.2:1.6})
                )}
                {t.badge>0&&(
                  <div style={{position:"absolute",top:0,right:active?2:4,minWidth:16,height:16,borderRadius:8,
                    background:V.danger,border:`2px solid ${V.navBg}`,display:"flex",alignItems:"center",justifyContent:"center",
                    padding:"0 4px"}}>
                    <span style={{fontSize:8,fontWeight:800,color:"#fff",fontFamily:V.mono}}>{t.badge>9?"9+":t.badge}</span>
                  </div>
                )}
              </div>
              <span style={{fontSize:10,fontWeight:active?700:500,color:active?V.accent:V.text3,
                fontFamily:V.font,letterSpacing:".02em"}}>{t.label}</span>
            </button>
          );
        })}
      </nav>}
      {!showSidebar&&activeMe?.powered_by&&<PoweredBy/>}
      <UndoToast d={d}/>
      <SuccessToast/>
      <GlobalConfirm/>
      <MessageBanner onTap={(msg)=>{
        if(msg.type==="dm"){d({type:"TAB",tab:"social_messages"});}
        else if(msg.type==="group"){d({type:"TAB",tab:"social_groups"});}
        else if(msg.type==="reauth"){ReauthCtrl.show();}
      }}/>
      <ReauthModal/>
      {/* Iron Score: Level-Up Celebration + Notification Checks */}
      {s.onboarded&&<GamificationOverlay s={s}/>}
      {/* Interactive Guide Overlay */}
      {guideActive&&guideStep>=0&&guideStep<guideSteps.length&&(()=>{
        const step=guideSteps[guideStep];
        const isFirst=guideStep===0;
        const isLast=guideStep===guideSteps.length-1;
        const labels=guideSteps.map(g=>({welcome:"Welcome",workout:"Workout",nutrition:"Nutrition",body:"Body",track:"Track",plan:"Plan",social:"Social",settings:"Settings",done:"Done"})[g.id]||g.id);
        return(
          <div style={{position:"fixed",bottom:0,left:0,right:0,zIndex:1800,padding:"0 10px",paddingBottom:"max(10px, env(safe-area-inset-bottom))",
            display:"flex",justifyContent:"center",pointerEvents:"none"}}>
            <div key={guideStep} role="dialog" aria-label={step.title} style={{pointerEvents:"auto",width:"100%",maxWidth:520,
              background:V.sheetBg,border:`1px solid ${V.cardBorder}`,borderRadius:22,boxShadow:V.shadowLg,padding:"16px 18px 14px",
              animation:"popIn .25s cubic-bezier(.2,.8,.3,1) both"}}>
              <ProgressSteps step={guideStep+1} labels={labels}/>
              <div style={{display:"flex",alignItems:"flex-start",gap:12,marginTop:14}}>
                <IconTile icon={step.ic||Icons.zap} size={42}/>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{fontSize:16,fontWeight:800,color:V.text,letterSpacing:"-0.01em",lineHeight:1.2}}>{step.title}</div>
                  <div style={{fontSize:13,color:V.text2,lineHeight:1.5,marginTop:4}}>{step.msg}</div>
                </div>
              </div>
              {step.hint&&!isFirst&&!isLast&&(
                <div style={{display:"flex",alignItems:"center",gap:8,marginTop:12,padding:"8px 12px",borderRadius:10,
                  background:`${V.accent}0f`,border:`1px solid ${V.accent}26`}}>
                  <span aria-hidden style={{width:7,height:7,borderRadius:4,background:V.accent,flexShrink:0,animation:"pulse 2s infinite"}}/>
                  <span style={{fontSize:12,color:V.accent,fontWeight:600}}>Try it: {step.hint}</span>
                </div>
              )}
              <div style={{display:"flex",gap:8,marginTop:14,alignItems:"center"}}>
                {!isFirst&&!isLast&&<Btn v="secondary" onClick={()=>setGuideStep(g=>g+1)} s={{minHeight:44}}>Skip</Btn>}
                <Btn full onClick={()=>{Haptic.light();if(isLast)completeGuide();else setGuideStep(g=>g+1);}} s={{minHeight:44}}>
                  {isFirst?"Let's go":isLast?"Start training":"Next step"} {Icons.chevRight({size:16,color:onAccent(),strokeWidth:2.6})}
                </Btn>
              </div>
              {!isFirst&&!isLast&&(
                <div style={{textAlign:"center",marginTop:2}}>
                  <LinkBtn color={V.text3} onClick={completeGuide} style={{fontSize:12}}>I know my way around — skip the tour</LinkBtn>
                </div>
              )}
            </div>
          </div>
        );
      })()}

      {/* Global Check-in Modal */}
      {showGlobalCheckin&&<CheckinModal s={s} d={d} onClose={()=>setShowGlobalCheckin(false)}/>}

      {/* Feature Help — "Show me how" bottom sheet */}
      {showFeatureHelp&&(()=>{
        const helps={
          home:[{l:"Readiness Score",d:"Daily recovery score based on check-ins, sleep, soreness, and training load."},
            {l:"Streak",d:"Consecutive days with logged workouts. Shows your record and how close you are to beating it."},
            {l:"Today's Plan",d:"Shows your scheduled workout with exercise list. One tap to start."},
            {l:"Accountability",d:"Partner nudges if you miss 2+ days. Set a partner in Friends."},
            {l:"Quick Actions",d:"Jump to log workout, nutrition, or weigh-in."}],
          log:[{l:"Workout Logger",d:"Add exercises, sets, weight, reps, RPE. Auto-fills with progressive overload suggestion. Shows last session's sets before adding."},
            {l:"Nutrition",d:"200+ foods, barcode scanner, custom items. Recent foods as quick-add chips. Save favorite meals for one-tap logging."},
            {l:"Body Metrics",d:"Weight, body fat, measurements. Trend arrows show changes. Copy yesterday for daily tracking."}],
          track:[{l:"Calendar",d:"Every session by day, with the last session of each type and quick day overrides."},
            {l:"PRs Page",d:"Every exercise PR with weight×reps, E1RM, date, and how close your recent lifts are."},
            {l:"Exercise Chart",d:"Select any exercise — see weight and E1RM over time as a line chart."},
            {l:"Duration Trends",d:"Average, longest, shortest workout times. Bar chart with trend line."},
            {l:"Volume Tracker",d:"Weekly sets per muscle group with recommended ranges (10-20 sets)."},
            {l:"Analytics",d:"Trends, readiness, strength score with per-lift standards (Beginner → Elite)."},
            {l:"Heat Map",d:"Visual muscle balance — see which muscles are overworked or neglected."},
            {l:"Compare",d:"Side-by-side before/after progress photos with time span."},
            {l:"Weekly Report",d:"Shareable summary card of your week's training, nutrition, and readiness."}],
          plan:[{l:"AI Coach",d:"Generates today's workout based on schedule, readiness, and progressive overload."},
            {l:"AI Chat",d:"Ask your coach anything. Has full context: phase, injuries, schedule, streak."},
            {l:"Programs",d:"PPL, 5/3/1, PHUL, nSuns, Bro Split, Full Body — with full exercise lists."},
            {l:"Schedule",d:"Set your weekly split. Supports overrides for individual days."},
            {l:"Phase Tracker",d:"Cut, bulk, maintenance tracking with date ranges."},
            {l:"Meal Plan",d:"Auto-generated daily meals hitting your macro targets."},
            {l:"Fast Food Hacks",d:"High-protein orders from 10+ chains with macro breakdowns."},
            {l:"Supplements",d:"Daily checklist for creatine, protein, vitamins, etc."},
            {l:"Goals",d:"Weight, strength, or custom targets with deadline tracking."},
            {l:"1RM Calculator",d:"Estimate max from any weight × reps."},
            {l:"Injury Manager",d:"Track injuries with affected areas. AI Coach adapts around them."},
            {l:"Substitutions",d:"Find alternative exercises for any movement."},
            {l:"Form Check",d:"Record and review exercise form with video."},
            {l:"Data Guard",d:"Catches suspicious data — weight jumps, duplicate entries, outliers."}],
          social:[{l:"Feed",d:"Your stats, friend activity, PR celebrations, and quick messages."},
            {l:"Friends",d:"Add by friend code, username, or email. Tap to see profile or chat."},
            {l:"DM Chat",d:"Private 1-on-1 messages with friends. 200 character limit. Quick reply chips."},
            {l:"Groups",d:"Create or join crews by code. Leaderboards, challenges, feed, and group chat."},
            {l:"Challenges",d:"Tiered badges: Streak, Big 3, Macro Master. Weekly rotating challenges."},
            {l:"Compare",d:"Side-by-side stat comparison with any friend."},
            {l:"Profile",d:"QR code, friend code, username, and privacy controls."},
            {l:"Badges",d:"Achievement collection from your training milestones."}],
          settings:[{l:"Theme",d:"Dark or light mode. Instant switch, no reload needed."},
            {l:"Units",d:"Switch between lbs and kg globally."},
            {l:"Goals",d:"Set daily calorie, protein, carbs, fat, TDEE, and workouts per week targets."},
            {l:"Schedule",d:"Configure your weekly training split."},
            {l:"Exercise Library",d:"Add custom exercises with category and YouTube links."},
            {l:"Profile",d:"Edit name, age, fitness level, and account details."},
            {l:"Backup & Restore",d:"Cloud sync with PIN protection. Restore on any device."},
            {l:"Privacy",d:"Control what friends can see: workouts, macros, photos, body."},
            {l:"Data Management",d:"Storage usage indicator, export all data as JSON, load demo data, clear data."},
            {l:"Cloud Sync",d:"Auto-syncs after every change. Manual sync available."}],
        };
        const items=helps[showFeatureHelp]||helps.home;
        return(
          <div style={{position:"fixed",top:0,left:0,right:0,bottom:0,zIndex:1900,display:"flex",alignItems:"flex-end"}}>
            <div style={{position:"absolute",top:0,left:0,right:0,bottom:0,background:"rgba(0,0,0,.7)"}} onClick={()=>setShowFeatureHelp(null)}/>
            <div style={{position:"relative",background:V.sheetBg,borderRadius:"20px 20px 0 0",
              padding:"20px 20px 32px",width:"100%",maxHeight:"70vh",overflowY:"auto",border:`1px solid ${V.cardBorder}`,borderBottom:"none"}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
                <div style={{fontSize:14,fontWeight:800,color:V.text}}>How this page works</div>
                <button onClick={()=>setShowFeatureHelp(null)} style={{background:"none",border:"none",cursor:"pointer",padding:4,fontSize:14,color:V.text3}}>✕</button>
              </div>
              {items.map((item,i)=>(
                <div key={i} style={{padding:"10px 0",borderBottom:i<items.length-1?`1px solid ${V.divider}`:"none"}}>
                  <div style={{fontSize:13,fontWeight:700,color:V.accent}}>{item.l}</div>
                  <div style={{fontSize:11,color:V.text3,marginTop:2,lineHeight:1.5}}>{item.d}</div>
                </div>
              ))}
            </div>
          </div>
        );
      })()}
      {/* #4: iOS Install Guide Modal */}
      {showInstallGuide&&(
        <InstallGuide isIOS={isIOS()} isAndroid={/Android/.test(navigator.userAgent)}
          onClose={()=>{setShowInstallGuide(false);LS.set("ft-install-dismissed",true);bumpBanner();}}/>
      )}
      {/* Push Notification Prompt — shown after tour */}
      {showNotifPrompt&&(
        <div style={{position:"fixed",top:0,left:0,right:0,bottom:0,zIndex:2000,display:"flex",alignItems:"center",justifyContent:"center",padding:20}}>
          <div style={{position:"absolute",top:0,left:0,right:0,bottom:0,background:"rgba(0,0,0,.7)",backdropFilter:"blur(8px)",animation:"fadeIn .2s ease-out"}}/>
          <div role="alertdialog" aria-modal="true" aria-label="Enable notifications" style={{position:"relative",
            background:V.sheetBg,borderRadius:22,padding:24,maxWidth:380,width:"100%",
            border:`1px solid ${V.cardBorder}`,boxShadow:V.shadowLg,animation:"popIn .25s cubic-bezier(.2,.8,.3,1) both"}}>
            <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:16}}>
              <IconTile icon={Icons.bell} size={48}/>
              <div style={{minWidth:0}}>
                <div style={{fontSize:19,fontWeight:800,color:V.text,letterSpacing:"-0.01em"}}>Stay on track</div>
                <div style={{fontSize:12,color:V.text3,marginTop:2,lineHeight:1.4}}>Smart reminders, only when they help</div>
              </div>
            </div>
            <div style={{display:"flex",flexDirection:"column",gap:4,padding:"6px 4px",borderRadius:14,background:V.cardGrad,border:`1px solid ${V.cardBorder}`,marginBottom:14}}>
              {[
                {icon:Icons.dumbbell,color:V.accent,text:"Log your workouts on training days"},
                {icon:Icons.fork,color:V.warn,text:"Track breakfast, lunch and dinner"},
                {icon:Icons.droplet,color:V.accent2,text:"Stay hydrated through the day"},
                {icon:Icons.activity,color:V.purple,text:"Complete your daily readiness check-in"},
                {icon:Icons.flame,color:V.warn,text:"Keep your streak alive"},
              ].map((item,i)=>(
                <div key={i} style={{display:"flex",alignItems:"center",gap:12,padding:"8px 10px"}}>
                  <IconTile icon={item.icon} color={item.color} size={30}/>
                  <span style={{fontSize:13,color:V.text}}>{item.text}</span>
                </div>
              ))}
            </div>
            <Callout icon={Icons.trophy} style={{marginBottom:16}}>
              Lifters with reminders on are <strong style={{color:V.text}}>3× more likely</strong> to hit their goals.
            </Callout>
            <Btn full onClick={handleNotifAccept} s={{minHeight:50,fontSize:15}}>Enable Notifications</Btn>
            <div style={{textAlign:"center",marginTop:6}}>
              <LinkBtn color={V.text3} onClick={handleNotifDecline}>Maybe later</LinkBtn>
            </div>
          </div>
        </div>
      )}
      {/* White-label: invite landing / redeem / consent, suspended notice */}
      {joinOverlay}
      {orgSuspended&&!suspendedAck&&s.onboarded&&(
        <SuspendedSheet orgName={orgMe?.org?.name} reason={orgMe?.org?.suspended_reason} onContinue={()=>{if(suspendAckKey)LS.set(suspendAckKey,true);setSuspendedAck(true);}}/>
      )}
    </div>
    </OrgContext.Provider>
  );
}

export default App;
